const QUANTITY_STEP = 1;
const SUBSCRIPTION_UPGRADE_SELLING_PLAN_ID = 2131919001;
const CROSS_SELL_FALLBACK_IMAGE = 'https://cdn.shopify.com/s/files/1/0680/3918/0441/files/c88591891f61a255e4705ee72e6534e410af7529.png?v=1774968255';
const GWP_THRESHOLD_KEY_PROPERTY = '_gwp_threshold_key';
const GWP_REWARD_TYPE_PROPERTY = '_gwp_reward_type';
const GWP_LOCKED_PROPERTY = '_gwp_locked';
// Longest the checkout button waits for the last gift check (handleCheckout).
// Adding a missing gift takes one /cart/add.js (~1 s) plus a /cart.js read.
const CHECKOUT_GWP_CHECK_TIMEOUT_MS = 3000;
// Cross-page handoff from the advertorial promotion CTA (behavior B). See
// openDrawerFromAdvertorialHandoff for how these survive the theme's one-time
// post-add sync reload.
const ADVERTORIAL_OPEN_DRAWER_KEY = 'elm:advertorial-open-drawer';
const ADVERTORIAL_OPEN_DRAWER_TTL_MS = 10000;
const ADVERTORIAL_OPEN_DRAWER_MAX_TRIES = 3;

class CartDrawerCustom extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    // Page variant: rendered inline on the /cart page instead of as an offcanvas drawer.
    // Skips open/close state, body scroll locking, overlay toggling, and legacy-trigger bridging.
    this.isPageVariant = this.classList.contains('cart-drawer-custom--page');
    this.legacyOldDrawerObserver = null;
    this.legacyOldDrawer = null;
    this.summaryBreakdownObserver = null;
    this.handleCartAddBound = this.handleCartAdd.bind(this);
    this.handleThemeCartUpdateBound = this.handleThemeCartUpdate.bind(this);
    this.handleLegacyReactCartClickBound = this.handleLegacyReactCartClick.bind(this);
    this.handleLegacyOffcanvasTriggerClickBound = this.handleLegacyOffcanvasTriggerClick.bind(this);
    this.handleLegacyCartAddEventBound = this.handleLegacyCartAddEvent.bind(this);
    this.handlePotentialAddToCartClickBound = this.handlePotentialAddToCartClick.bind(this);
    this.handlePotentialAddToCartSubmitBound = this.handlePotentialAddToCartSubmit.bind(this);
    this.initLegacyOldDrawerObserverBound = this.initLegacyOldDrawerObserver.bind(this);
    this.handleDialogKeydownBound = this.handleDialogKeydown.bind(this);
    this._lastFocusedElement = null;
    this.crossSellRules = this.loadCrossSellRules();
    this.activeCrossSellRule = null;
    this.gwpThresholdSets = this.loadGwpThresholdSets();
    this.gwpUpsells = this.loadGwpUpsells();
    this.isEnforcingGwpGifts = false;
    this.isWatchingPotentialAddToCart = false;
    // handleExternalAdd bookkeeping (adds started by theme buttons that run
    // their own /cart/add.js, e.g. new-product-grid.js).
    this.externalAddsInFlight = 0;
    this.isRefreshingDrawerContent = false;
    this.refreshDrawerQueuePromise = null;
    // Set by enforceGwpGiftThresholds: did its last run add/remove a gift line?
    this.gwpEnforceMutatedCart = false;
    this.isBundleSwapping = false;
    this.pendingRefreshDrawerContent = false;
    this.pendingRefreshCartState = null;
    this.refreshDrawerContentPromise = null;
    this.latestCartState = null;
    this._fetchCartStatePromise = null;
    this.bindEvents();
  }

  connectedCallback() {
    if (this.isSubscriptionSavingsDebugEnabled()) {
      console.info('[CartDrawerCustom] initialized: subscription savings debug enabled');
    }

    // Listen for cart add events
    document.addEventListener('cart:added', this.handleCartAddBound);

    // Listen for theme-native cart updates (e.g. PDP product-form add to cart)
    document.addEventListener('cart:update', this.handleThemeCartUpdateBound);

    if (!this.isPageVariant) {
      // Listen for cart drawer toggle clicks
      const cartToggle = document.querySelector('[data-cart-drawer-toggle]');
      if (cartToggle) {
        cartToggle.addEventListener('click', this.open.bind(this));
      }

      // Bridge legacy cart icon and offcanvas cart triggers to this drawer.
      document.addEventListener('click', this.handleLegacyReactCartClickBound, true);
      document.addEventListener('click', this.handleLegacyOffcanvasTriggerClickBound, true);

      // Bridge legacy cart:add event name to the new cart:added event.
      document.documentElement.addEventListener('cart:add', this.handleLegacyCartAddEventBound);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initLegacyOldDrawerObserverBound, { once: true });
      } else {
        this.initLegacyOldDrawerObserver();
      }
    }

    // Watch for add-to-cart flows on both variants so the /cart page also refreshes
    // when the user adds a product from elsewhere in the theme (e.g. the header
    // "START NOW" CTA inside react-cart-icon, a PDP add-to-cart, a collection tile).
    document.addEventListener('click', this.handlePotentialAddToCartClickBound, true);
    document.addEventListener('submit', this.handlePotentialAddToCartSubmitBound, true);

    if (this.isPageVariant) {
      this.classList.add('cart-drawer-custom--page-right-pending');
    }

    // Kick off an early cart fetch so window.__sharedCartState is populated before
    // cart-upsell.js runs its first renderUpsell, avoiding a redundant second fetch.
    this.fetchCartState();

    const initialSyncPromise = this.syncCrossSellFromCart();

    // Cross-page handoff: an advertorial promotion CTA (behavior B) added a product
    // then navigated here (the PDP). Open the drawer on arrival so the user lands with
    // it already open, showing the just-added item.
    if (!this.isPageVariant) {
      this.openDrawerFromAdvertorialHandoff();
    }

    if (this.isPageVariant) {
      Promise.allSettled([
        this.waitForCartStylesheetReady(),
        initialSyncPromise,
      ]).finally(() => {
        this.classList.remove('cart-drawer-custom--page-right-pending');
      });

      // Clean up orphan gifts on the /cart page load — if the cart was left with only
      // gift items (e.g. user emptied it in the drawer on another tab, or a previous
      // threshold rule was renamed), enforce + refresh so the page shows the real state.
      this.cleanupOrphanGiftsOnLoad();
    }
  }

  openDrawerFromAdvertorialHandoff() {
    let payload = null;
    try {
      payload = sessionStorage.getItem(ADVERTORIAL_OPEN_DRAWER_KEY);
    } catch (error) {
      return;
    }
    if (!payload) return;

    // Payload is "<timestamp>:<tries>". The flag is intentionally NOT cleared on open
    // so it survives the theme's one-time post-add sync reload (the drawer re-opens on
    // the reloaded page). It is cleared when the user closes the drawer, when it expires,
    // or after a few tries — the last two guard against any reload loop.
    const parts = `${payload}`.split(':');
    const timestamp = Number(parts[0]);
    const tries = Number(parts[1] || 0);

    const isStale = !Number.isFinite(timestamp) || (Date.now() - timestamp) > ADVERTORIAL_OPEN_DRAWER_TTL_MS;
    if (isStale || tries >= ADVERTORIAL_OPEN_DRAWER_MAX_TRIES) {
      this.clearAdvertorialHandoff();
      return;
    }

    try {
      sessionStorage.setItem(ADVERTORIAL_OPEN_DRAWER_KEY, `${timestamp}:${tries + 1}`);
    } catch (error) {
      /* ignore */
    }

    // Open immediately on arrival (mirrors watchPotentialAddToCart) so the drawer is
    // visible even if the content refresh below triggers GWP/cross-sell churn. open()
    // is guarded against double-opening, so the refresh's own open() call is a no-op.
    this.openAvailableDrawer();
    // Then fetch the fresh cart and render the just-added item into the drawer.
    this.handleCartAdd();

    // Self-clean: if no reload happens shortly after opening, drop the flag so the
    // drawer does not re-open on a later same-tab navigation. If the theme's post-add
    // sync reload fires first, this timeout never runs (page unloads) and the flag
    // survives so the drawer re-opens on the reloaded page.
    window.setTimeout(() => this.clearAdvertorialHandoff(), 2500);
  }

  clearAdvertorialHandoff() {
    try {
      sessionStorage.removeItem(ADVERTORIAL_OPEN_DRAWER_KEY);
    } catch (error) {
      /* ignore */
    }
  }

  async cleanupOrphanGiftsOnLoad() {
    try {
      const cart = await this.fetchCartState();
      if (!cart?.items?.length) return;
      const hasRegularItems = cart.items.some((item) => `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}` === '');
      const hasGiftItems = cart.items.some((item) => `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}` !== '');
      if (!hasRegularItems && hasGiftItems) {
        await this.refreshDrawerContent(cart);
      }
    } catch (error) {
      console.error('cleanupOrphanGiftsOnLoad failed:', error);
    }
  }

  isCartStylesheetLoaded() {
    const cssLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
    return cssLinks.some((link) => {
      const href = `${link.getAttribute('href') || ''}`;
      if (!href.includes('cart-drawer-custom.css')) return false;
      return Boolean(link.sheet);
    });
  }

  waitForCartStylesheetReady(timeoutMs = 1800) {
    if (this.isCartStylesheetLoaded()) {
      return Promise.resolve();
    }

    const cssLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .filter((link) => `${link.getAttribute('href') || ''}`.includes('cart-drawer-custom.css'));

    if (!cssLinks.length) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let isSettled = false;
      const settle = () => {
        if (isSettled) return;
        isSettled = true;
        cssLinks.forEach((link) => {
          link.removeEventListener('load', settle);
          link.removeEventListener('error', settle);
        });
        resolve();
      };

      cssLinks.forEach((link) => {
        link.addEventListener('load', settle, { once: true });
        link.addEventListener('error', settle, { once: true });
      });

      window.setTimeout(settle, timeoutMs);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('cart:added', this.handleCartAddBound);
    document.removeEventListener('cart:update', this.handleThemeCartUpdateBound);
    document.removeEventListener('click', this.handlePotentialAddToCartClickBound, true);
    document.removeEventListener('submit', this.handlePotentialAddToCartSubmitBound, true);
    document.removeEventListener('keydown', this.handleDialogKeydownBound, true);
    if (!this.isPageVariant) {
      document.removeEventListener('click', this.handleLegacyReactCartClickBound, true);
      document.removeEventListener('click', this.handleLegacyOffcanvasTriggerClickBound, true);
      document.documentElement.removeEventListener('cart:add', this.handleLegacyCartAddEventBound);
      document.removeEventListener('DOMContentLoaded', this.initLegacyOldDrawerObserverBound);
    }

    if (this.legacyOldDrawerObserver) {
      this.legacyOldDrawerObserver.disconnect();
      this.legacyOldDrawerObserver = null;
    }
    if (this.summaryBreakdownObserver) {
      this.summaryBreakdownObserver.disconnect();
      this.summaryBreakdownObserver = null;
    }
    this.legacyOldDrawer = null;
  }

  /** @param {Event} event */
  handleLegacyReactCartClick(event) {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    const cartIconButton = target?.closest('react-cart-icon button');
    if (!cartIconButton) return;

    // Don't intercept the Start Now / add-to-cart form button
    if (target?.closest('react-cart-icon form')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.open();
  }

  /** @param {Event} event */
  handleLegacyOffcanvasTriggerClick(event) {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    const trigger = target?.closest('.js-offcanvas-trigger[data-drawer-name="cart"]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    this.open();
  }

  handleLegacyCartAddEvent() {
    document.dispatchEvent(new CustomEvent('cart:added'));
  }

  initLegacyOldDrawerObserver() {
    const oldDrawer = document.querySelector('.js-offcanvas[data-drawer-names~="cart"]');
    if (!oldDrawer) return;

    if (this.legacyOldDrawerObserver) {
      this.legacyOldDrawerObserver.disconnect();
      this.legacyOldDrawerObserver = null;
    }

    this.legacyOldDrawer = oldDrawer;
    this.legacyOldDrawerObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName !== 'class') continue;
        if (!this.legacyOldDrawer) return;

        const drawerClasses = this.legacyOldDrawer.classList;
        const isLegacyDrawerOpening = drawerClasses.contains('is-entering') || drawerClasses.contains('is-expanded');
        if (!isLegacyDrawerOpening) continue;

        const closeBtn = this.legacyOldDrawer.querySelector('.js-offcanvas-close');
        if (closeBtn instanceof HTMLElement) {
          closeBtn.click();
        }

        document.body.classList.remove('is-frozen', 'has-open-offcanvas');
        this.open();
        break;
      }
    });

    this.legacyOldDrawerObserver.observe(this.legacyOldDrawer, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  bindEvents() {
    // Close button
    const closeBtn = this.querySelector('.cart-drawer-custom__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.close.bind(this));
    }

    // Overlay
    const overlay = document.getElementById('cart-drawer-overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    // Continue shopping button
    const continueBtn = this.querySelector('.cart-drawer-custom__continue');
    if (continueBtn) {
      continueBtn.addEventListener('click', this.close.bind(this));
    }

    // Checkout button
    const checkoutBtn = this.querySelector('.cart-drawer-custom__checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', this.handleCheckout.bind(this));
    }

    // Discount apply button
    const discountBtn = this.querySelector('.cart-drawer-custom__discount-button');
    if (discountBtn) {
      discountBtn.addEventListener('click', this.handleDiscountApply.bind(this));
    }

    // Remove buttons
    const removeBtns = this.querySelectorAll('.cart-drawer-custom__remove-btn');
    removeBtns.forEach(btn => {
      btn.addEventListener('click', this.handleRemoveItem.bind(this));
    });

    // Quantity buttons (bind to container for event delegation)
    const itemsContainer = this.querySelector('.cart-drawer-custom__items');
    if (itemsContainer) {
      itemsContainer.addEventListener('click', this.handleQuantityChange.bind(this));
      itemsContainer.addEventListener('change', this.handleQuantityInputChange.bind(this));
      itemsContainer.addEventListener('keydown', this.handleQuantityInputKeydown.bind(this));
      itemsContainer.addEventListener('focusout', this.handleQuantityInputFocusOut.bind(this));
    }

    const bundleActionButton = this.querySelector('.cart-drawer-custom__bundle-offer-action');
    if (bundleActionButton) {
      bundleActionButton.addEventListener('click', this.handleBundleOfferClick.bind(this));
    }

    const subscribeUpgradeButtons = this.querySelectorAll('.cart-drawer-custom__item-subscribe-upgrade');
    subscribeUpgradeButtons.forEach((button) => {
      button.addEventListener('click', this.handleSubscribeUpgrade.bind(this));
    });

    const bonusAddButtons = this.querySelectorAll('.cart-drawer-custom__item-bonus-action');
    bonusAddButtons.forEach((button) => {
      button.addEventListener('click', this.handleBonusAdd.bind(this));
    });

    const sellingPlanSelects = this.querySelectorAll('.cart-drawer-custom__item-selling-plan-select');
    sellingPlanSelects.forEach((select) => {
      select.addEventListener('change', this.handleSellingPlanChange.bind(this));
    });

    const gwpRedeemButtons = this.querySelectorAll('[data-gwp-redeem-button]');
    gwpRedeemButtons.forEach((button) => {
      button.addEventListener('click', this.handleGwpRedeemClick.bind(this));
    });

    const gwpUpsellAddButton = this.querySelector('[data-gwp-upsell-add]');
    if (gwpUpsellAddButton) {
      gwpUpsellAddButton.addEventListener('click', this.handleGwpUpsellAddClick.bind(this));
    }

    this.bindOffersToggle();
    this.bindSummaryBreakdownToggle();
    this.bindSummaryBreakdownObserver();

  }

  bindSummaryBreakdownObserver() {
    const summaryPanel = this.querySelector('.cart-drawer-custom__summary-breakdown-panel');

    if (this.summaryBreakdownObserver) {
      this.summaryBreakdownObserver.disconnect();
      this.summaryBreakdownObserver = null;
    }

    if (!summaryPanel) return;

    this.summaryBreakdownObserver = new MutationObserver(() => {
      this.updateSummaryBreakdownTitle();
    });

    this.summaryBreakdownObserver.observe(summaryPanel, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    this.updateSummaryBreakdownTitle();
  }

  bindOffersToggle() {
    this.bindOffersToggleByNamespace('offers');
    this.bindOffersToggleByNamespace('offers-two');
  }

  /**
   * @param {'offers' | 'offers-two'} namespace
   */
  bindOffersToggleByNamespace(namespace) {
    const toggleButton = /** @type {HTMLButtonElement | null} */ (this.querySelector(`.cart-drawer-custom__${namespace}-toggle`));
    const panel = /** @type {HTMLElement | null} */ (this.querySelector(`.cart-drawer-custom__${namespace}-panel`));
    const toggleLabel = /** @type {HTMLElement | null} */ (this.querySelector(`.cart-drawer-custom__${namespace}-toggle-label`));

    if (!toggleButton || !panel || !toggleLabel) return;

    const setExpandedState = (isExpanded) => {
      toggleButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      toggleLabel.textContent = isExpanded ? 'CLOSE' : 'SEE OFFERS';
      if (namespace === 'offers') {
        panel.classList.toggle('show', isExpanded);
        return;
      }

      panel.classList.toggle('is-collapsed', !isExpanded);
    };

    const isInitiallyExpanded = toggleButton.getAttribute('aria-expanded') !== 'false';
    setExpandedState(isInitiallyExpanded);

    toggleButton.addEventListener('click', () => {
      const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
      setExpandedState(!isExpanded);
    });
  }

  bindSummaryBreakdownToggle() {
    const toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('.cart-drawer-custom__summary-breakdown-toggle'));
    const panel = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer-custom__summary-breakdown-panel'));
    if (!toggle || !panel) return;

    const setExpandedState = (isExpanded) => {
      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      panel.classList.toggle('is-collapsed', !isExpanded);
    };

    const isInitiallyExpanded = toggle.getAttribute('aria-expanded') !== 'false';
    setExpandedState(isInitiallyExpanded);

    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      setExpandedState(!isExpanded);
    });
  }

  async handleCartAdd() {
    if (this.isBundleSwapping) return;
    if (this.isRefreshingDrawerContent) return;
    // If the watcher is already polling for the cart change, let it handle the refresh
    // with the detected cart state — avoids refreshing with a stale latestCartState.
    if (this.isWatchingPotentialAddToCart) return;
    const cart = await this.fetchCartState();
    await this.refreshDrawerContent(cart);
    this.openAvailableDrawer();
  }

  /** @param {CustomEvent<{ data?: { didError?: boolean; source?: string } }>} event */
  async handleThemeCartUpdate(event) {
    if (event?.detail?.data?.didError) return;
    if (event?.detail?.data?.source === 'cart-drawer-custom') return;
    if (this.isBundleSwapping) return;
    if (this.isRefreshingDrawerContent) return;
    if (this.isWatchingPotentialAddToCart) return;

    const cart = await this.fetchCartState();
    if (cart?.item_count !== undefined) {
      this.updateCartCountUI(Number(cart.item_count) || 0);
    }

    await this.refreshDrawerContent(cart);
    this.openAvailableDrawer();
  }

  openAvailableDrawer() {
    if (this.isPageVariant) return;

    if (this.isConnected) {
      this.open();
      return;
    }

    const fallbackTrigger = /** @type {HTMLElement | null} */ (
      document.querySelector('[data-cart-drawer-toggle], .js-open-cart-drawer-trigger, .js-offcanvas-trigger[data-drawer-name="cart"]')
    );

    if (fallbackTrigger) {
      fallbackTrigger.click();
    }
  }

  /** @param {{ item_count?: number; total_price?: number; items?: Array<any> } | null} cart */
  getCartStateSignature(cart) {
    if (!cart) return '';

    const itemSignature = (cart.items || [])
      .map((item) => {
        const key = `${item?.key || item?.variant_id || ''}`;
        const quantity = Number(item?.quantity || 0);
        const sellingPlanId = Number(
          item?.selling_plan_allocation?.selling_plan?.id
          || item?.selling_plan_allocation?.selling_plan_id
          || 0
        );
        return `${key}:${quantity}:${sellingPlanId}`;
      })
      .sort()
      .join('|');

    return `${Number(cart.item_count || 0)}::${Number(cart.total_price || 0)}::${itemSignature}`;
  }

  /** @param {Event} event */
  handlePotentialAddToCartClick(event) {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (!target) return;

    const addToCartButton = target.closest(
      '.tvg_core_project_components_add_to_cart_4b09a12a4888984a_button, .tvg_core_react_product_form_W6HjyG_addToCartButton'
    );
    // Also catch the react-cart-icon "START NOW" CTA (class tvg_react_cart_icon_C_zLTW_ctaBtn
    // on a <button type="submit"> inside <react-cart-icon>). Without this match the /cart
    // page would not refresh when the user adds the featured product from the header.
    const cartIconCtaButton = target.closest('react-cart-icon form button, react-cart-icon form [type="submit"]');
    // Also catch the smart upsell CTA (cart-upsell.js), which runs its own /cart/add.js
    // instead of submitting a form and only re-renders its own card afterwards. Nothing
    // else refreshes the cart UI after that add: it used to repaint only when
    // bundle-watcher happened to swap the added product into a bundle and emitted
    // cart:added. Its "direct bundle add" path does not emit, so adding the Elemental Lip
    // Collection from a shade left stale line items and totals until a reload.
    const cartUpsellButton = target.closest('[data-cart-upsell-button]');

    if (!addToCartButton && !cartIconCtaButton && !cartUpsellButton) return;

    this.watchPotentialAddToCart();
  }

  /** @param {Event} event */
  handlePotentialAddToCartSubmit(event) {
    const form = /** @type {HTMLFormElement | null} */ (event.target instanceof HTMLFormElement ? event.target : null);
    if (!form) return;

    const action = `${form.getAttribute('action') || ''}`.toLowerCase();
    if (!action.includes('/cart/add')) return;

    const hasKnownButton = Boolean(
      form.querySelector('.tvg_core_project_components_add_to_cart_4b09a12a4888984a_button, .tvg_core_react_product_form_W6HjyG_addToCartButton')
    );
    // Also treat any form inside react-cart-icon posting to /cart/add as an add trigger
    // (the header "START NOW" CTA submits that way but uses its own button classname).
    const isCartIconForm = Boolean(form.closest('react-cart-icon'));

    if (!hasKnownButton && !isCartIconForm) return;

    this.watchPotentialAddToCart();
  }

  async watchPotentialAddToCart() {
    if (this.isWatchingPotentialAddToCart) return;
    this.isWatchingPotentialAddToCart = true;

    // Open immediately with a loading overlay so the user gets instant feedback
    // without seeing the cart being built item by item.
    this.openAvailableDrawer();
    this.classList.add('cart-drawer-custom--adding');

    try {
      // Use cached state as baseline — avoids a fetch before the user's product is even added.
      const beforeCart = this.latestCartState || await this.fetchCartState();
      const beforeSignature = this.getCartStateSignature(beforeCart);
      const beforeCount = Number(beforeCart?.item_count || 0);

      let detectedCart = null;
      let candidateCart = null;
      let candidateSignature = '';
      let attempts = 0;
      while (attempts < 8 && !detectedCart) {
        // First poll at 100ms, subsequent at 200ms — balances speed vs extra requests.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          window.setTimeout(resolve, attempts === 0 ? 100 : 200);
        });

        // eslint-disable-next-line no-await-in-loop
        const nextCart = await this.fetchCartState();
        const nextSignature = this.getCartStateSignature(nextCart);
        const nextCount = Number(nextCart?.item_count || 0);
        const isSwapping = Boolean(window.__isSwapping);
        const isTransientEmpty = beforeCount > 0 && nextCount === 0;

        if (nextSignature && nextSignature !== beforeSignature) {
          // Ignore interim empty states while bundle swap removes components before adding final bundle.
          if (!isSwapping && !isTransientEmpty) {
            // One stable read is enough — add-to-cart is a single deterministic operation.
            candidateCart = nextCart;
            candidateSignature = nextSignature;
            detectedCart = candidateCart;
            break;
          }
        }

        attempts += 1;
      }

      if (!detectedCart && candidateCart && Number(candidateCart?.item_count || 0) > 0) {
        detectedCart = candidateCart;
      }

      if (!detectedCart) return;

      this.updateCartCountUI(Number(detectedCart?.item_count || 0));
      await this.refreshDrawerContent(detectedCart);
    } catch (error) {
      console.error('Error watching add-to-cart flow:', error);
    } finally {
      this.classList.remove('cart-drawer-custom--adding');
      this.isWatchingPotentialAddToCart = false;
    }
  }

  /**
   * Hand-off for theme buttons that run their own /cart/add.js request — the
   * `.js-ajax-add-to-cart` buttons of new-product-grid / storytelling-grid
   * (new-product-grid.js). The drawer stays closed until the product is
   * actually in the cart, then opens already showing it: callers ask Shopify
   * to bundle this section's render into the add request (`sections` param)
   * and that markup is applied before the drawer slides in, so the common
   * case has no loading state at all. Only when no render came back (a legacy
   * caller, a failed section render, or another refresh mid-flight) does the
   * drawer open with the loading overlay while it fetches its markup.
   *
   * GWP enforcement then runs on a live /cart.js; if it adds or removes a gift
   * the drawer re-renders once more.
   *
   * While an external add is in flight the watcher flag is raised, so the
   * cart:add / cart:added / cart:update events other scripts fire don't start
   * a second refresh, and cart-upsell.js defers to this DOM swap.
   *
   * @param {Promise<any>} addRequest resolves with the /cart/add.js JSON once the add succeeded, rejects on failure
   * @returns {Promise<boolean>} true when the drawer refreshed with the new cart
   */
  async handleExternalAdd(addRequest) {
    this.externalAddsInFlight += 1;
    this.isWatchingPotentialAddToCart = true;

    let result = null;
    try {
      result = await addRequest;
    } catch (_error) {
      // Nothing changed in the cart; the caller reports the failure.
      this.releaseExternalAdd();
      return false;
    }

    try {
      const sectionId = this.getSectionRenderingId();
      const prerenderedHtml = sectionId && typeof result?.sections?.[sectionId] === 'string'
        ? result.sections[sectionId]
        : null;

      // refreshDrawerContent applies a prerender synchronously when the queue
      // is idle; otherwise the queued re-run fetches fresh markup and the
      // overlay covers that wait.
      const needsOverlay = !prerenderedHtml || this.isRefreshingDrawerContent;
      if (needsOverlay) this.classList.add('cart-drawer-custom--adding');

      const refreshPromise = this.refreshDrawerContent(null, { prerenderedHtml });
      this.openAvailableDrawer();
      await refreshPromise;
      return true;
    } catch (error) {
      console.error('Error refreshing cart drawer after add-to-cart:', error);
      return false;
    } finally {
      this.releaseExternalAdd();
    }
  }

  releaseExternalAdd() {
    this.externalAddsInFlight = Math.max(0, this.externalAddsInFlight - 1);
    if (this.externalAddsInFlight === 0) {
      this.classList.remove('cart-drawer-custom--adding');
      this.isWatchingPotentialAddToCart = false;
    }
  }

  /** @param {number} itemCount */
  updateCartCountUI(itemCount) {
    const count = Number.isFinite(itemCount) ? itemCount : 0;

    const cartCounts = document.querySelectorAll('.header__cart-count');
    cartCounts.forEach((element) => {
      element.textContent = count.toString();
    });

    // Sync React cart icon badge
    const reactCartCounts = document.querySelectorAll('.tvg_react_cart_icon_C_zLTW_count');
    reactCartCounts.forEach((element) => {
      element.textContent = count.toString();
    });

    const drawerCount = this.querySelector('.cart-drawer-custom__count');
    if (drawerCount) {
      drawerCount.textContent = count.toString();
    }
  }

  /** @param {{ item_count: number; total_price: number }} cart */
  emitCartUpdated(cart) {
    const detailPayload = {
      cart,
      data: {
        cart,
        item_count: Number(cart?.item_count || 0),
        didError: false,
        source: 'cart-drawer-custom',
      },
    };

    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: detailPayload,
    }));

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: detailPayload,
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: detailPayload,
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: detailPayload,
    }));
  }

  /**
   * A gift the drawer shows as unlocked can still be missing from the cart. On
   * a brand-new cart, Intelligems writes its cart metafield (cartMetafieldsSet)
   * while the gift's /cart/add.js is in flight, and Shopify can keep the write
   * that has no gift. Nothing re-checks the cart until its next change, so the
   * order would go through without the gift. Run the gift enforcement once more
   * on the live cart before leaving; checkout never waits longer than
   * CHECKOUT_GWP_CHECK_TIMEOUT_MS and goes ahead if the check fails.
   *
   * @param {Event} event
   */
  async handleCheckout(event) {
    const target = /** @type {HTMLButtonElement | null} */ (event?.currentTarget);
    if (target?.disabled || target?.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    if (this.isCheckingOut) return;
    this.isCheckingOut = true;
    target?.setAttribute('aria-busy', 'true');

    try {
      await Promise.race([
        this.ensureGwpGiftsBeforeCheckout(),
        new Promise((resolve) => {
          window.setTimeout(resolve, CHECKOUT_GWP_CHECK_TIMEOUT_MS);
        }),
      ]);
    } catch (_error) {
      // The gift check never blocks checkout.
    }

    window.location.href = '/checkout';
    // Reset right away: if the page comes back from the back/forward cache the
    // button has to work again.
    this.isCheckingOut = false;
    target?.removeAttribute('aria-busy');
  }

  async ensureGwpGiftsBeforeCheckout() {
    // A drawer refresh in flight runs its own enforcement; let it settle first.
    if (this.refreshDrawerQueuePromise) {
      await this.refreshDrawerQueuePromise;
    }

    // enforceGwpGiftThresholds returns without acting while another run holds
    // the lock, so wait for that run to finish instead of skipping the check.
    for (let attempt = 0; attempt < 20 && this.isEnforcingGwpGifts; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        window.setTimeout(resolve, 100);
      });
    }

    // The sets were filtered by date when last loaded; reload them so a page left
    // open across a campaign boundary (BFCM tiers) checks against the tiers live now.
    this.gwpThresholdSets = this.loadGwpThresholdSets();
    await this.enforceGwpGiftThresholds();
  }

  handleDiscountApply() {
    const discountField = /** @type {HTMLInputElement | null} */ (this.querySelector('.cart-drawer-custom__discount-field'));
    const discountCode = discountField?.value?.trim();

    if (discountCode) {
      // Apply discount code logic here
      console.log('Applying discount code:', discountCode);
      // You would typically make an API call to apply the discount
    }
  }

  /** @param {HTMLElement | null} card @param {boolean} isLoading */
  setCardLoadingState(card, isLoading) {
    if (!card) return;

    card.classList.toggle('cart-drawer-custom__loading-card--loading', isLoading);
    if (isLoading) {
      card.setAttribute('aria-busy', 'true');
      return;
    }

    card.removeAttribute('aria-busy');
  }

  /** @param {HTMLElement | null} itemCard @param {boolean} isLoading */
  setItemLoadingState(itemCard, isLoading) {
    if (!itemCard) return;

    itemCard.classList.toggle('cart-drawer-custom__item--loading', isLoading);
    if (isLoading) {
      itemCard.setAttribute('aria-busy', 'true');
      return;
    }

    itemCard.removeAttribute('aria-busy');
  }

  /** @param {Event} event */
  async handleRemoveItem(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const removeButton = target?.closest('.cart-drawer-custom__remove-btn');
    if (!removeButton) return;

    const itemContainer = /** @type {HTMLElement | null} */ (removeButton.closest('.cart-drawer-custom__item'));
    if (itemContainer?.classList.contains('cart-drawer-custom__item--loading')) return;

    // @ts-ignore - dataset property access
    const lineItemKey = removeButton.dataset.lineItemKey;
    // @ts-ignore - dataset property access
    const variantId = removeButton.dataset.variantId;

    if (lineItemKey || variantId) {
      this.setItemLoadingState(itemContainer, true);
      await this.updateCartItem(lineItemKey || variantId, 0, itemContainer);
    }
  }

  /** @param {Event} event */
  async handleQuantityChange(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const quantityButton = target?.closest('.cart-drawer-custom__quantity-btn');
    if (!quantityButton) return;

    // @ts-ignore - closest method return type
    const itemContainer = quantityButton.closest('.cart-drawer-custom__item');
    // @ts-ignore - dataset property access
    const variantId = itemContainer?.dataset.variantId;
    const isIncrement = quantityButton.classList.contains('increment');

    if (itemContainer?.dataset.gwpLocked === 'true') return;

    if (variantId) {
      if (itemContainer.classList.contains('cart-drawer-custom__item--loading')) return;

      const quantityInput = /** @type {HTMLInputElement | null} */ (itemContainer.querySelector('.cart-drawer-custom__quantity-input'));
      const currentQuantity = this.getItemDisplayQuantity(itemContainer);
      const quantityStepValue = /** @type {HTMLElement | null} */ (itemContainer)?.dataset.quantityStep;
      const step = parseInt(quantityStepValue || `${QUANTITY_STEP}`, 10) || QUANTITY_STEP;
      const minimumQuantity = step;
      const newQuantity = isIncrement
        ? currentQuantity + step
        : currentQuantity <= minimumQuantity ? 0 : currentQuantity - step;

      if (quantityInput) {
        quantityInput.value = `${Math.max(newQuantity, 0)}`;
      }

      this.setItemLoadingState(itemContainer, true);

      const lineItemKey = /** @type {HTMLElement | null} */ (itemContainer)?.dataset.lineItemKey;
      await this.updateCartItem(lineItemKey || variantId, newQuantity, itemContainer);
    }
  }

  /** @param {HTMLElement | null} itemContainer */
  getItemDisplayQuantity(itemContainer) {
    if (!itemContainer) return 0;

    const quantityInput = /** @type {HTMLInputElement | null} */ (itemContainer.querySelector('.cart-drawer-custom__quantity-input'));
    const inputValue = Number.parseInt(`${quantityInput?.value || ''}`.replace(/[^0-9]/g, ''), 10);
    if (Number.isFinite(inputValue) && inputValue >= 0) {
      return inputValue;
    }

    const dataQuantity = Number.parseInt(itemContainer.dataset.quantity || '0', 10);
    return Number.isFinite(dataQuantity) && dataQuantity >= 0 ? dataQuantity : 0;
  }

  /** @param {string} rawValue @param {number} fallback */
  normalizeQuantityInputValue(rawValue, fallback = 1) {
    const numericValue = Number.parseInt(`${rawValue || ''}`.replace(/[^0-9]/g, ''), 10);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return fallback;
    }

    return numericValue;
  }

  /** @param {HTMLInputElement | null} input @param {HTMLElement | null} itemContainer */
  async commitQuantityInput(input, itemContainer) {
    if (!input || !itemContainer) return;
    if (itemContainer.dataset.gwpLocked === 'true') {
      input.value = `${this.getItemDisplayQuantity(itemContainer) || 1}`;
      return;
    }

    if (itemContainer.classList.contains('cart-drawer-custom__item--loading')) return;

    const variantId = `${itemContainer.dataset.variantId || ''}`;
    const lineItemKey = `${itemContainer.dataset.lineItemKey || ''}`;
    if (!variantId && !lineItemKey) return;

    const currentQuantity = Number.parseInt(itemContainer.dataset.quantity || '1', 10) || 1;
    const nextQuantity = this.normalizeQuantityInputValue(input.value, currentQuantity);
    input.value = `${nextQuantity}`;

    if (nextQuantity === currentQuantity) return;

    this.setItemLoadingState(itemContainer, true);
    await this.updateCartItem(lineItemKey || variantId, nextQuantity, itemContainer);
  }

  /** @param {Event} event */
  handleQuantityInputChange(event) {
    const input = /** @type {HTMLInputElement | null} */ (event.target instanceof HTMLInputElement ? event.target : null);
    if (!input?.matches('.cart-drawer-custom__quantity-input')) return;

    input.value = `${input.value || ''}`.replace(/[^0-9]/g, '');
  }

  /** @param {KeyboardEvent} event */
  handleQuantityInputKeydown(event) {
    const input = /** @type {HTMLInputElement | null} */ (event.target instanceof HTMLInputElement ? event.target : null);
    if (!input?.matches('.cart-drawer-custom__quantity-input')) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      const itemContainer = /** @type {HTMLElement | null} */ (input.closest('.cart-drawer-custom__item'));
      this.commitQuantityInput(input, itemContainer);
      input.blur();
      return;
    }

    if (event.key === 'Escape') {
      const itemContainer = /** @type {HTMLElement | null} */ (input.closest('.cart-drawer-custom__item'));
      input.value = `${Number.parseInt(itemContainer?.dataset.quantity || '1', 10) || 1}`;
      input.blur();
    }
  }

  /** @param {FocusEvent} event */
  handleQuantityInputFocusOut(event) {
    const input = /** @type {HTMLInputElement | null} */ (event.target instanceof HTMLInputElement ? event.target : null);
    if (!input?.matches('.cart-drawer-custom__quantity-input')) return;

    const itemContainer = /** @type {HTMLElement | null} */ (input.closest('.cart-drawer-custom__item'));
    this.commitQuantityInput(input, itemContainer);
  }

  open() {
    if (this.isPageVariant) return;
    if (this.isOpen) return;

    document.body.classList.remove('has-open-offcanvas', 'is-frozen');
    document.documentElement.classList.remove('has-open-offcanvas', 'is-frozen');

    this.isOpen = true;
    this.classList.add('open');

    const overlay = document.getElementById('cart-drawer-overlay');
    if (overlay) {
      overlay.classList.add('open');
    }

    // A11y: modal semantics only while open. This element stays rendered when
    // closed (display:flex, visibility:visible), so a static aria-modal would
    // hide the entire page from assistive tech permanently.
    this.setAttribute('aria-modal', 'true');

    const activeElement = document.activeElement;
    this._lastFocusedElement = (activeElement instanceof HTMLElement && activeElement !== document.body)
      ? activeElement
      : null;

    document.addEventListener('keydown', this.handleDialogKeydownBound, true);

    // Focus the drawer container, not a child: refreshDrawerContent() calls
    // replaceWith() on the header/content/footer on every cart update, which
    // would destroy a focused child mid-interaction.
    window.requestAnimationFrame(() => {
      if (!this.isOpen) return;
      this.focus({ preventScroll: true });
    });

    // Lock body scroll — position:fixed is required on iOS Safari to prevent
    // background scroll bleed-through that overflow:hidden alone does not stop.
    this._savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (this.isPageVariant) return;
    if (!this.isOpen) return;

    // The user dismissed the drawer — drop any advertorial handoff intent so it does
    // not re-open on a later navigation within the TTL window.
    this.clearAdvertorialHandoff();

    this.isOpen = false;
    this.classList.remove('open');

    document.body.classList.remove('has-open-offcanvas', 'is-frozen');
    document.documentElement.classList.remove('has-open-offcanvas', 'is-frozen');

    const overlay = document.getElementById('cart-drawer-overlay');
    if (overlay) {
      overlay.classList.remove('open');
    }

    this.removeAttribute('aria-modal');
    document.removeEventListener('keydown', this.handleDialogKeydownBound, true);

    // Restore body scroll position
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this._savedScrollY || 0);

    // Return focus to whatever opened the drawer.
    const elementToRestore = this._lastFocusedElement;
    this._lastFocusedElement = null;
    if (elementToRestore?.isConnected) {
      elementToRestore.focus({ preventScroll: true });
  }
  }

  /** @param {KeyboardEvent} event */
  handleDialogKeydown(event) {
    if (!this.isOpen || this.isPageVariant) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      // The quantity input has its own Escape behaviour (revert value + blur)
      // in handleQuantityInputKeydown. Let that win instead of closing the drawer.
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest('.cart-drawer-custom__quantity-input')) return;
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = this.getFocusableElements();
    if (!focusables.length) {
      event.preventDefault();
      this.focus({ preventScroll: true });
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (!this.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && (active === first || active === this)) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getFocusableElements() {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(this.querySelectorAll(selector)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hasAttribute('inert')) return false;
      if (element.closest('[aria-hidden="true"]')) return false;
      return element.getClientRects().length > 0;
    });
  }

  renderEmptyState() {
    const content = this.querySelector('.cart-drawer-custom__content');
    if (content) {
      content.innerHTML = `
        <div class="cart-drawer-custom__empty">
          <h2>Your cart is empty</h2>
          <p>Add some products to get started!</p>
        </div>
      `;
    }

    const footer = this.querySelector('.cart-drawer-custom__footer');
    if (footer) {
      footer.remove();
    }
  }

  /** @param {{ items?: Array<{ product_id?: number; variant_id?: number; quantity?: number }> } | null} [cartData] */
  /**
   * @param {object | null} [cartData] cart JSON already in hand (e.g. a /cart/update.js response)
   * @param {{ prerenderedHtml?: string | null }} [options] see refreshDrawerContentInternal
   */
  async refreshDrawerContent(cartData = null, options = {}) {
    if (this.isRefreshingDrawerContent) {
      this.pendingRefreshDrawerContent = true;
      if (cartData) {
        this.pendingRefreshCartState = cartData;
      }

      // Wait for the whole queue — including the re-run just requested — not
      // only the iteration currently in flight, so callers (handleExternalAdd's
      // loading overlay, updateCartItem's row spinner) resolve once *their*
      // cart state is actually on screen.
      if (this.refreshDrawerQueuePromise) {
        await this.refreshDrawerQueuePromise;
      }
      return;
    }

    this.isRefreshingDrawerContent = true;
    let resolveQueue = () => {};
    this.refreshDrawerQueuePromise = new Promise((resolve) => {
      resolveQueue = resolve;
    });

    try {
      let nextCartData = cartData;
      let nextOptions = options;

      do {
        this.pendingRefreshDrawerContent = false;
        const pendingCartState = this.pendingRefreshCartState;
        this.pendingRefreshCartState = null;
        if (pendingCartState) {
          nextCartData = pendingCartState;
        }

        this.refreshDrawerContentPromise = this.refreshDrawerContentInternal(nextCartData, nextOptions);
        await this.refreshDrawerContentPromise;
        nextCartData = null;
        // A queued re-run always renders fresh markup — any prerender the first
        // call brought along predates whatever queued the re-run.
        nextOptions = {};
      } while (this.pendingRefreshDrawerContent);
    } catch (error) {
      console.error('Error refreshing cart drawer content:', error);
    } finally {
      this.refreshDrawerContentPromise = null;
      this.isRefreshingDrawerContent = false;
      this.refreshDrawerQueuePromise = null;
      resolveQueue();
    }
  }

  /**
   * Section Rendering API id of the section this element lives in
   * (`sections--…__cart_drawer_custom_xxx` for the footer-group drawer,
   * `template--…__cart_page_custom_xxx` for the /cart page variant).
   */
  getSectionRenderingId() {
    const sectionEl = this.closest('.shopify-section');
    const id = `${sectionEl?.id || ''}`;
    const prefix = 'shopify-section-';
    return id.startsWith(prefix) ? id.slice(prefix.length) : '';
  }

  /**
   * Fresh markup for this drawer. The refresh used to re-download the whole
   * page (≈880 KB / ~1.2 s on the home) just to pick its own markup out of it;
   * asking Shopify for this one section returns ≈50 KB in ~150–250 ms. The
   * full page stays as the fallback when the id can't be resolved or the
   * section request fails.
   *
   * @returns {Promise<string>}
   */
  async fetchDrawerMarkup() {
    const headers = { 'X-Requested-With': 'XMLHttpRequest' };
    const sectionId = this.getSectionRenderingId();

    if (sectionId) {
      const params = new URLSearchParams(window.location.search);
      params.set('section_id', sectionId);
      try {
        const sectionResponse = await fetch(`${window.location.pathname}?${params.toString()}`, { headers });
        if (sectionResponse.ok) {
          return sectionResponse.text();
        }
      } catch (_error) {
        // fall through to the full page
      }
    }

    const pageResponse = await fetch(`${window.location.pathname}${window.location.search}`, { headers });
    if (!pageResponse.ok) {
      throw new Error('Failed to refresh cart drawer content');
    }
    return pageResponse.text();
  }

  /**
   * @param {{ items?: Array<{ product_id?: number; variant_id?: number; quantity?: number }> } | null} [cartData]
   * @param {{ prerenderedHtml?: string | null }} [options] `prerenderedHtml`: this section's markup
   *   already rendered by Shopify as part of the mutation that triggered the refresh (bundled
   *   section rendering). Applied synchronously, so a caller can open the drawer with the new
   *   content and no loading state.
   */
  async refreshDrawerContentInternal(cartData = null, options = {}) {
    const prerenderedHtml = typeof options?.prerenderedHtml === 'string' && options.prerenderedHtml
      ? options.prerenderedHtml
      : null;
    const contentContainer = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer-custom__content'));
    const previousScrollTop = contentContainer ? contentContainer.scrollTop : 0;
    const previousContentHeight = contentContainer ? contentContainer.getBoundingClientRect().height : 0;
    const summarySnapshot = this.captureSummarySnapshot();
    const upsellSnapshot = this.captureUpsellSnapshot();

    if (contentContainer && previousContentHeight > 0) {
      contentContainer.style.minHeight = `${Math.ceil(previousContentHeight)}px`;
    }

    // GWP enforcement (a live /cart.js, plus gift mutations when a threshold
    // was crossed) and the markup request used to run back to back. Now the
    // markup is either already here (prerendered) or requested in parallel
    // with the enforcement, and only re-fetched when enforcement changed the cart.
    let markupPromise = null;
    const prerenderApplied = prerenderedHtml
      ? this.applyDrawerMarkup(prerenderedHtml, summarySnapshot, upsellSnapshot)
      : false;
    if (!prerenderApplied) {
      markupPromise = this.fetchDrawerMarkup();
    }

    this.gwpEnforceMutatedCart = false;
    const syncedCart = await this.enforceGwpGiftThresholds(cartData);

    if (this.gwpEnforceMutatedCart) {
      // A gift line came or went — whatever was rendered so far predates it.
      if (markupPromise) await markupPromise.catch(() => null);
      if (!this.applyDrawerMarkup(await this.fetchDrawerMarkup(), summarySnapshot, upsellSnapshot)) return;
    } else if (markupPromise) {
      if (!this.applyDrawerMarkup(await markupPromise, summarySnapshot, upsellSnapshot)) return;
    }

    const finalCartState = syncedCart || cartData || await this.fetchCartState();
    if (finalCartState) {
      // Paints the cross-sell tile, GWP progress/rewards/upsell and the summary
      // from this cart. enforceGwpGiftThresholds already ran on a live fetch
      // above, so the syncCrossSellFromCart() that used to follow here only
      // re-fetched /cart.js and re-enforced the same state — one more
      // round-trip before cart:updated for nothing.
      this.renderDynamicCartState(finalCartState);
    } else {
      await this.syncCrossSellFromCart(null);
    }

    if (finalCartState?.item_count !== undefined) {
      this.updateCartCountUI(Number(finalCartState.item_count) || 0);
      this.emitCartUpdated(finalCartState);
    }

    const nextContentContainer = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer-custom__content'));
    if (nextContentContainer) {
      nextContentContainer.scrollTop = previousScrollTop;
      nextContentContainer.style.minHeight = '';
    }
  }

  /**
   * Swap this element's header/content/footer (or, on the /cart page variant,
   * the whole inner tree) for the ones in `html`, then re-bind events and
   * re-apply the summary/upsell snapshots taken before the swap.
   *
   * @param {string} html a full page or a Section Rendering API fragment containing <cart-drawer-custom>
   * @returns {boolean} false when `html` holds no drawer markup (nothing was touched)
   */
  applyDrawerMarkup(html, summarySnapshot, upsellSnapshot) {
    const parser = new DOMParser();
    const nextDocument = parser.parseFromString(html, 'text/html');
    // Match the same variant (page or drawer) so we don't accidentally pull the page markup
    // into the drawer (or vice versa) when the fetched document contains both instances.
    const nextDrawerSelector = this.isPageVariant
      ? 'cart-drawer-custom.cart-drawer-custom--page'
      : 'cart-drawer-custom:not(.cart-drawer-custom--page)';
    const nextDrawer = /** @type {HTMLElement | null} */ (
      nextDocument.querySelector(nextDrawerSelector) || nextDocument.querySelector('cart-drawer-custom')
    );

    if (!nextDrawer) return false;

    if (this.isPageVariant) {
      // Page variant: the layout has sibling wrappers (__page-layout / __page-right /
      // __page-offers-mobile) that piecewise header/content/footer swaps would leave
      // behind when the cart becomes empty. Replace the whole inner tree instead so the
      // empty ↔ non-empty transition leaves no stale markup.
      this.innerHTML = nextDrawer.innerHTML;
    } else {
      const currentHeader = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer-custom__header'));
      const nextHeader = /** @type {HTMLElement | null} */ (nextDrawer.querySelector('.cart-drawer-custom__header'));
      if (currentHeader && nextHeader) {
        currentHeader.replaceWith(nextHeader.cloneNode(true));
      }

      const currentContent = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer-custom__content'));
      const nextContent = /** @type {HTMLElement | null} */ (nextDrawer.querySelector('.cart-drawer-custom__content'));
      if (currentContent && nextContent) {
        currentContent.replaceWith(nextContent.cloneNode(true));
      }

      const currentFooter = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer-custom__footer'));
      const nextFooter = /** @type {HTMLElement | null} */ (nextDrawer.querySelector('.cart-drawer-custom__footer'));
      if (currentFooter && nextFooter) {
        currentFooter.replaceWith(nextFooter.cloneNode(true));
      } else if (currentFooter && !nextFooter) {
        currentFooter.remove();
      } else if (!currentFooter && nextFooter) {
        this.appendChild(nextFooter.cloneNode(true));
      }
    }

    this.bindEvents();
    this.applySummarySnapshot(summarySnapshot);
    this.applyUpsellSnapshot(upsellSnapshot);
    return true;
  }

  /** @param {string} variantId @param {number} quantity @param {Element | null} [itemContainer] */
  async updateCartItem(variantId, quantity, itemContainer = null) {
    if (quantity < 0) return;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: quantity } }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();

      this.updateCartCountUI(cart.item_count);

      // refreshDrawerContent dispatches cart:updated with the post-enforcement cart
      // (correct item_count after any orphan gift cleanup). Don't re-emit here with
      // the pre-enforcement `cart` — TVG's react cart icon listens for cart:updated
      // and would re-render with a stale count (e.g. "1" showing after the gift was
      // already removed by enforcement).
      await this.refreshDrawerContent(cart);

    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      if (itemContainer && itemContainer.isConnected) {
        this.setItemLoadingState(/** @type {HTMLElement} */ (itemContainer), false);
      }
    }
  }

  /** @param {number} cents */
  formatMoney(cents) {
    // Basic money formatting - you might want to use Shopify's formatMoney
    return `$${(cents / 100).toFixed(2)}`;
  }

  isSubscriptionSavingsDebugEnabled() {
    try {
      const winFlag = Boolean(window?.__CART_SUBSCRIPTION_SAVINGS_DEBUG__);
      const storageFlag = localStorage.getItem('cart:subscription-savings:debug') === '1';
      return winFlag || storageFlag;
    } catch (_error) {
      return false;
    }
  }

  logSubscriptionSavingsDebug(payload) {
    if (!this.isSubscriptionSavingsDebugEnabled()) return;
    try {
      console.groupCollapsed('[CartDrawerCustom] Subscription Savings debug');
      console.table(payload?.lineItems || []);
      console.log('totalSavingsCents', payload?.totalSavingsCents || 0);
      console.log('totalSavingsDisplay', this.formatMoney(payload?.totalSavingsCents || 0));
      console.groupEnd();
    } catch (_error) {
      // no-op: debug logging should never break cart behavior
    }
  }

  /** @param {{ items?: Array<any>; items_subtotal_price?: number; total_price?: number } | null} cart */
  getThresholdSubtotalCents(cart) {
    if (!cart?.items?.length) {
      return Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
    }

    const subtotalFromOriginalLines = cart.items.reduce((total, item) => {
      const isGiftLine = `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}` !== '';
      if (isGiftLine) return total;

      const originalLinePrice = Number(item?.original_line_price ?? 0);
      if (Number.isFinite(originalLinePrice) && originalLinePrice > 0) {
        return total + originalLinePrice;
      }

      const fallbackLinePrice = Number(item?.final_line_price ?? item?.line_price ?? 0);
      return total + (Number.isFinite(fallbackLinePrice) ? Math.max(0, fallbackLinePrice) : 0);
    }, 0);

    if (subtotalFromOriginalLines > 0) {
      return subtotalFromOriginalLines;
    }

    return Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
  }

  /** @param {{ items?: Array<any>; total_price?: number } | null} cart */
  getDisplayTotalCents(cart) {
    const baseTotalCents = Number(cart?.total_price || 0);
    if (!cart?.items?.length) return Math.max(0, baseTotalCents);

    const giftLineTotalCents = cart.items.reduce((total, item) => {
      const thresholdKey = `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}`;
      const rewardType = `${item?.properties?.[GWP_REWARD_TYPE_PROPERTY] || ''}`.toLowerCase();
      const isGiftLine = thresholdKey !== '' || rewardType === 'gift';
      if (!isGiftLine) return total;

      const lineCents = Number(item?.final_line_price ?? item?.line_price ?? 0);
      if (!Number.isFinite(lineCents) || lineCents <= 0) return total;
      return total + lineCents;
    }, 0);

    return Math.max(0, baseTotalCents - giftLineTotalCents);
  }

  loadCrossSellRules() {
    const rulesNode = document.getElementById('CrossSellRulesData');
    if (!rulesNode) return [];

    try {
      const parsed = JSON.parse(rulesNode.textContent || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch (error) {
      console.error('Failed to parse CrossSellRulesData:', error);
      return [];
    }
  }

  loadGwpThresholdSets() {
    const offerBlock = this.querySelector('[data-gwp-offer-block]');
    const offerTwoBlock = this.querySelector('[data-gwp-offer-two-block]');
    const scriptPayload = this.parseJsonScriptData('GwpThresholdsData');

    const scriptOfferThresholds = Array.isArray(scriptPayload?.offer) ? scriptPayload.offer : [];
    const scriptOfferTwoThresholds = Array.isArray(scriptPayload?.offer_two) ? scriptPayload.offer_two : [];

    const offerJson =
      offerBlock?.getAttribute('data-gwp-offer-thresholds-json')
      || scriptPayload?.offer?.thresholds_json
      || scriptPayload?.offer_thresholds_json
      || '[]';

    const offerTwoJson =
      offerTwoBlock?.getAttribute('data-gwp-offer-two-thresholds-json')
      || scriptPayload?.offer_two?.thresholds_json
      || scriptPayload?.offer_two_thresholds_json
      || '[]';

    const offer = scriptOfferThresholds.length
      ? this.normalizeThresholds(scriptOfferThresholds, 'offer')
      : this.parseThresholdsJson(offerJson, 'offer');
    const offerTwo = scriptOfferTwoThresholds.length
      ? this.normalizeThresholds(scriptOfferTwoThresholds, 'offer-two')
      : this.parseThresholdsJson(offerTwoJson, 'offer-two');

    // Campaign tiers (e.g. the BFCM gift thresholds) carry a date window: only the ones
    // running now count. The rest are kept apart so enforceGwpGiftThresholds can take
    // their gifts back out of carts once the window closes.
    const nowSeconds = Date.now() / 1000;
    const isInWindow = (threshold) => this.isThresholdInWindow(threshold, nowSeconds);

    return {
      offer: offer.filter(isInWindow),
      offerTwo: offerTwo.filter(isInWindow),
      outOfWindow: [...offer, ...offerTwo].filter((threshold) => !isInWindow(threshold)),
    };
  }

  /**
   * Whether a threshold's date window (snippets/gwp-threshold-window) is open. Liquid
   * checked it too, but against the time the page was rendered, which a cached page lags.
   * @param {{ active_from?: number; active_until?: number; window_valid?: boolean }} threshold
   * @param {number} nowSeconds
   */
  isThresholdInWindow(threshold, nowSeconds) {
    if (threshold?.window_valid === false) return false;
    const activeFrom = Number(threshold?.active_from || 0);
    const activeUntil = Number(threshold?.active_until || 0);
    if (activeFrom > 0 && nowSeconds < activeFrom) return false;
    if (activeUntil > 0 && nowSeconds >= activeUntil) return false;
    return true;
  }

  /** @param {string} raw @param {'offer' | 'offer-two'} namespace */
  parseThresholdsJson(raw, namespace) {
    if (!raw || typeof raw !== 'string') return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return this.normalizeThresholds(parsed, namespace);
    } catch (error) {
      console.error(`Failed to parse ${namespace} thresholds JSON:`, error);
      return [];
    }
  }

  /** @param {Array<any>} thresholds @param {'offer' | 'offer-two'} namespace */
  normalizeThresholds(thresholds, namespace) {
    return thresholds
      .map((threshold, index) => {
        const amountRaw = Number(threshold?.amount || 0);
        const amount = Number.isFinite(amountRaw) ? (amountRaw < 1000 ? amountRaw * 100 : amountRaw) : 0;
        const rewardType = `${threshold?.reward_type || ''}`.toLowerCase();
        const giftVariantId = Number(threshold?.gift_variant_id || 0);
        const key = `${threshold?.key || `${namespace}-${index}`}`;

        return {
          key,
          amount,
          reward_type: rewardType === 'shipping' ? 'shipping' : 'gift',
          gift_variant_id: Number.isFinite(giftVariantId) ? giftVariantId : 0,
          shipping_savings_cents: Number(threshold?.shipping_savings_cents || 0),
          gift_display_value_cents: Number(threshold?.gift_display_value_cents || 0),
          reward_image_url: threshold?.reward_image_url || null,
          reward_name: rewardType === 'shipping' ? 'Complimentary shipping' : (threshold?.reward_name || 'Gift reward'),
          reward_description: threshold?.reward_description || '',
          message: threshold?.message || 'You are $[amount] away from [reward]',
          icon_url: threshold?.icon_url || null,
          active_from: Number(threshold?.active_from || 0),
          active_until: Number(threshold?.active_until || 0),
          window_valid: threshold?.window_valid !== false,
        };
      })
      .filter((threshold) => threshold.amount > 0)
      .sort((a, b) => a.amount - b.amount);
  }

  /** @param {'offer' | 'offer-two'} namespace */
  getThresholdsForNamespace(namespace) {
    if (namespace === 'offer') {
      return this.gwpThresholdSets?.offer || [];
    }
    return this.gwpThresholdSets?.offerTwo || [];
  }

  getAllGwpThresholds() {
    const seen = new Set();
    return [...this.getThresholdsForNamespace('offer'), ...this.getThresholdsForNamespace('offer-two')]
      .filter((threshold) => {
        const key = `${threshold.key || ''}`;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));
  }

  loadGwpUpsells() {
    const parsed = this.parseJsonScriptData('GwpUpsellsData');
    if (!Array.isArray(parsed)) return [];
    return parsed;
  }

  /** @param {string} scriptId */
  parseJsonScriptData(scriptId) {
    const scriptTag = document.getElementById(scriptId);
    if (!scriptTag) return null;

    try {
      return JSON.parse(scriptTag.textContent || 'null');
    } catch (error) {
      console.error(`Failed to parse ${scriptId}:`, error);
      return null;
    }
  }

  /** @param {{ items?: Array<any>; items_subtotal_price?: number; total_price?: number } | null} cart */
  renderDynamicCartState(cart) {
    if (!cart) return;

    this.latestCartState = cart;
    window.__sharedCartState = { data: cart, ts: Date.now() };
    this.gwpThresholdSets = this.loadGwpThresholdSets();

    const hasClaimOffSubscriptionInCart = this.hasClaimOffSubscriptionInCart(cart);
    const nextRule = hasClaimOffSubscriptionInCart ? this.getActiveCrossSellRule(cart) : null;
    this.activeCrossSellRule = nextRule;
    this.renderCrossSellTile(nextRule, cart);

    const subtotalCents = this.getThresholdSubtotalCents(cart);
    this.renderGwpProgress(subtotalCents);
    this.renderGwpRewards(cart, subtotalCents);
    this.renderGwpUpsell(cart);
    this.updateSummaryDynamic(cart);
  }

  /** @param {{ items?: Array<{ product_id?: number; variant_id?: number; quantity?: number }> } | null} [cartOverride] */
  async syncCrossSellFromCart(cartOverride = null) {
    if (!this.crossSellRules.length) {
      this.renderCrossSellTile(null, cartOverride);
      await this.syncGwpFromCart(cartOverride);
      return;
    }

    try {
      let cart = cartOverride || this.latestCartState || await this.fetchCartState();

      const hasClaimOffSubscriptionInCart = this.hasClaimOffSubscriptionInCart(cart);
      const nextRule = hasClaimOffSubscriptionInCart ? this.getActiveCrossSellRule(cart) : null;
      this.activeCrossSellRule = nextRule;
      this.renderCrossSellTile(nextRule, cart);

      await this.syncGwpFromCart(cart);
    } catch (error) {
      console.error('Error syncing cross-sell tile:', error);
    }
  }

  /** @param {{ items?: Array<{ variant_id?: number; quantity?: number }>; items_subtotal_price?: number; total_price?: number } | null} [cartOverride] */
  async syncGwpFromCart(cartOverride = null) {
    this.gwpThresholdSets = this.loadGwpThresholdSets();

    let cart = cartOverride || this.latestCartState || await this.fetchCartState();
    if (!cart) return;

    const syncedCart = await this.enforceGwpGiftThresholds(cart);
    if (syncedCart) {
      cart = syncedCart;
    }

    this.latestCartState = cart;

    const subtotalCents = this.getThresholdSubtotalCents(cart);
    this.renderGwpProgress(subtotalCents);
    this.renderGwpRewards(cart, subtotalCents);
    this.renderGwpUpsell(cart);
    this.updateSummaryDynamic(cart);
  }

  /** @param {{ items?: Array<any>; items_subtotal_price?: number; total_price?: number } | null} [cartOverride] */
  async enforceGwpGiftThresholds(cartOverride = null) {
    if (this.isEnforcingGwpGifts) {
      return cartOverride;
    }

    const giftThresholds = this.getAllGwpThresholds().filter((threshold) => {
      return threshold.reward_type === 'gift' && Number(threshold.amount) > 0 && Number(threshold.gift_variant_id) > 0;
    });

    // Continue even if there are no configured gift thresholds so the orphan-cleanup
    // below still runs (stale gifts from a previous/renamed rule would otherwise persist).

    this.isEnforcingGwpGifts = true;

    try {
      // Always re-fetch live cart state instead of trusting cartOverride/latestCartState.
      // This function mutates the cart (adds/removes gift lines), and both cartOverride and
      // latestCartState can be stale by the time this actually runs — e.g. refreshDrawerContent's
      // pending-refresh queue re-invokes this with a cart snapshot captured before a previous,
      // just-completed run of this same function already added the gift, which duplicated the
      // gift line (same _gwp_threshold_key twice) and put affected orders on hold. fetchCartState()
      // already dedupes concurrent in-flight requests, so this doesn't add extra network chatter
      // beyond what's needed for correctness here.
      let cart = await this.fetchCartState();
      if (!cart) {
        cart = cartOverride || this.latestCartState;
      }
      if (!cart) return cartOverride;

      // Every decision below is taken on this one snapshot and then applied in
      // at most TWO requests: one /cart/update.js zeroing every gift line that
      // has to go, one /cart/add.js with every gift that has to come in. The
      // previous loop issued a /cart/change.js per removed line and a
      // /cart/add.js + /cart.js per added gift, all sequential — crossing both
      // gift thresholds in one add cost ~2s of extra drawer loading. Gift lines
      // are $0, so adding/removing them never moves the threshold subtotal,
      // which is what makes the single-snapshot evaluation equivalent.
      const giftKeyOf = (item) => `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}`;
      const allItems = cart.items || [];
      const regularItems = allItems.filter((item) => giftKeyOf(item) === '');
      const giftItems = allItems.filter((item) => giftKeyOf(item) !== '');

      /** @type {Set<string>} line keys to set to quantity 0 */
      const removals = new Set();
      /** @type {Array<{ id: number; quantity: number; properties: Record<string, string> }>} */
      const additions = [];
      const isScheduledForRemoval = (item) => removals.has(`${item?.key || ''}`);

      // Safety net: if the cart has no regular (non-gift) items, remove ALL items tagged
      // with _gwp_threshold_key — regardless of whether their key still matches a configured
      // threshold. This handles orphan gifts left over from renamed/removed rules and the
      // "user emptied the cart" case where gifts should never be the only thing remaining.
      if (regularItems.length === 0 && giftItems.length > 0) {
        for (const line of giftItems) {
          const lineKey = `${line?.key || ''}`;
          if (lineKey) removals.add(lineKey);
        }
      }

      // A gift from a tier whose date window closed (e.g. the BFCM pouch after the last
      // stage) has no active rule left to take it out below. Remove it, unless a tier
      // running now gives the same gift: that one then decides whether it stays.
      const givesGift = (threshold, item) => (
        giftKeyOf(item) === `${threshold.key}`
        || (Number(threshold.gift_variant_id || 0) > 0 && Number(item?.variant_id || 0) === Number(threshold.gift_variant_id))
      );
      const outOfWindowGiftThresholds = (this.gwpThresholdSets?.outOfWindow || [])
        .filter((threshold) => threshold.reward_type === 'gift');
      for (const line of giftItems) {
        if (isScheduledForRemoval(line)) continue;
        if (giftThresholds.some((threshold) => givesGift(threshold, line))) continue;
        if (!outOfWindowGiftThresholds.some((threshold) => givesGift(threshold, line))) continue;
        const lineKey = `${line?.key || ''}`;
        if (lineKey) removals.add(lineKey);
      }

      const subtotalCents = this.getThresholdSubtotalCents(cart);

      for (const threshold of giftThresholds) {
        const isReached = subtotalCents >= Number(threshold.amount || 0);
        const thresholdGiftVariantId = Number(threshold.gift_variant_id || 0);
        // Match gift lines by threshold.key OR by the threshold's gift variant_id. The
        // variant-based match catches stale gifts whose _gwp_threshold_key points at a
        // block.id that no longer exists (rule renamed, section config changed, or the
        // item was originally added by the drawer section with a different block.id) —
        // without it those orphan gifts would linger forever because the strict key
        // match would never find them and subtotal drops would skip the remove branch.
        const matchingGiftLines = giftItems.filter((item) => {
          if (isScheduledForRemoval(item)) return false;
          if (giftKeyOf(item) === `${threshold.key}`) return true;
          return thresholdGiftVariantId > 0 && Number(item?.variant_id || 0) === thresholdGiftVariantId;
        });

        if (!isReached && matchingGiftLines.length > 0) {
          for (const line of matchingGiftLines) {
            const lineKey = `${line?.key || ''}`;
            if (lineKey) removals.add(lineKey);
          }
        } else if (isReached && matchingGiftLines.length === 0) {
          const giftVariantId = thresholdGiftVariantId;
          if (giftVariantId <= 0) continue;

          // Dedupe guard: the same gift variant may already be in the cart tagged with
          // a different threshold key (e.g. added by the drawer section's threshold block,
          // whose block.id differs from the page section's block.id even when they map to
          // the same product), or already queued by a previous threshold in this pass.
          // Skip the add instead of creating a duplicate line.
          const alreadyInCart = giftItems.some((item) => (
            !isScheduledForRemoval(item) && Number(item?.variant_id || 0) === giftVariantId
          ));
          const alreadyQueued = additions.some((item) => item.id === giftVariantId);
          if (alreadyInCart || alreadyQueued) continue;

          additions.push({
            id: giftVariantId,
            quantity: 1,
            properties: {
              [GWP_THRESHOLD_KEY_PROPERTY]: threshold.key,
              [GWP_REWARD_TYPE_PROPERTY]: 'gift',
              [GWP_LOCKED_PROPERTY]: 'true',
            },
          });
        }
      }

      let changed = false;

      if (removals.size > 0) {
        const updates = {};
        removals.forEach((lineKey) => { updates[lineKey] = 0; });
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (response.ok) {
          changed = true;
          // /cart/update.js returns the full updated cart — use it directly.
          const nextCart = await response.json();
          if (nextCart) { cart = nextCart; this.latestCartState = cart; }
        }
      }

      if (additions.length > 0) {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: additions }),
        });
        if (addResponse.ok) {
          changed = true;
          // /cart/add.js only returns added items — need full cart here.
          const nextCart = await this.fetchCartState();
          if (nextCart) { cart = nextCart; }
        }
      }

      this.gwpEnforceMutatedCart = changed;
      return changed ? cart : cartOverride || cart;
    } catch (error) {
      console.error('Error enforcing GWP gift thresholds:', error);
      return cartOverride;
    } finally {
      this.isEnforcingGwpGifts = false;
    }
  }

  fetchCartState() {
    // If a fetch is already in-flight, return the same promise so concurrent callers
    // (connectedCallback + syncCrossSellFromCart + watcher) share one network request.
    if (this._fetchCartStatePromise) return this._fetchCartStatePromise;

    this._fetchCartStatePromise = (async () => {
      try {
        const response = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        const cart = await response.json();
        this.latestCartState = cart;
        window.__sharedCartState = { data: cart, ts: Date.now() };
        return cart;
      } catch (_error) {
        return null;
      } finally {
        this._fetchCartStatePromise = null;
      }
    })();

    return this._fetchCartStatePromise;
  }

  /**
   * Shows the scheduled milestones (snippets/cart-offers-milestones) whose date window is
   * open now and hides the rest, then marks the last one on show, which sits at the end of
   * the bar instead of half past it.
   */
  syncScheduledMilestones() {
    const nowSeconds = Date.now() / 1000;
    this.querySelectorAll('[data-gwp-offer-milestones]').forEach((milestonesNode) => {
      milestonesNode.querySelectorAll('[data-gwp-active-from]').forEach((milestone) => {
        const el = /** @type {HTMLElement} */ (milestone);
        el.hidden = !this.isThresholdInWindow({
          active_from: Number(el.dataset.gwpActiveFrom || 0),
          active_until: Number(el.dataset.gwpActiveUntil || 0),
          window_valid: !el.hasAttribute('data-gwp-window-invalid'),
        }, nowSeconds);
      });

      const milestones = Array.from(milestonesNode.querySelectorAll(
        '.cart-drawer-custom__offers-progress-milestone:not(.cart-drawer-custom__offers-progress-milestone--offers)'
      ));
      const lastShown = milestones.filter((milestone) => !(/** @type {HTMLElement} */ (milestone)).hidden).pop();
      milestones.forEach((milestone) => {
        milestone.classList.toggle('cart-drawer-custom__offers-progress-milestone--last', milestone === lastShown);
      });
    });
  }

  /** @param {number} subtotalCents */
  renderGwpProgress(subtotalCents) {
    this.syncScheduledMilestones();

    const hasAnySubscription = this.hasAnySubscriptionInCart(this.latestCartState);
    const shippingThresholdAmount = this.getAllGwpThresholds()
      .filter((threshold) => `${threshold?.reward_type || ''}`.toLowerCase() === 'shipping')
      .map((threshold) => Number(threshold?.amount || 0))
      .find((amount) => Number.isFinite(amount) && amount > 0) || 0;

    const effectiveSubtotalCents = hasAnySubscription && shippingThresholdAmount > 0
      ? Math.max(subtotalCents, shippingThresholdAmount)
      : subtotalCents;

    const allThresholds = this.getAllGwpThresholds()
      .map((threshold) => Number(threshold?.amount || 0))
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((a, b) => a - b);

    if (!allThresholds.length) return;

    const highestAmount = allThresholds[allThresholds.length - 1];
    const globalFill = Math.max(0, Math.min(100, (effectiveSubtotalCents / highestAmount) * 100));

    // Iterate over ALL offer_two progress instances — there may be a duplicate
    // (e.g. page variant renders a mobile-only copy above the items) that needs the same updates.
    this.querySelectorAll('[data-gwp-offer-two-progress]').forEach((offerTwoProgress) => {
      /** @type {HTMLElement} */ (offerTwoProgress).style.setProperty('--offers-two-progress-fill', `${globalFill}`);
    });

    const offerProgressNodes = this.querySelectorAll('[data-gwp-offer-progress]');
    if (!offerProgressNodes.length) return;

    const offerThresholds = this.getThresholdsForNamespace('offer')
      .filter((threshold) => Number(threshold?.amount || 0) > 0)
      .sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));

    const segmentCount = offerThresholds.length;
    const segmentSize = segmentCount > 0 ? 100 / segmentCount : 100;

    const segmentsHtml = offerThresholds.length
      ? offerThresholds.map((threshold, index) => {
          // Each segment covers its own dollar range (previous threshold -> this one).
          // The milestones are laid out at equal widths, so filling by a single global
          // percentage made a segment look complete before its reward was unlocked.
          const segmentStartCents = index === 0 ? 0 : Number(offerThresholds[index - 1]?.amount || 0);
          const segmentEndCents = Number(threshold?.amount || 0);
          const segmentRangeCents = Math.max(1, segmentEndCents - segmentStartCents);
          const segmentFill = Math.max(0, Math.min(100, ((effectiveSubtotalCents - segmentStartCents) / segmentRangeCents) * 100));
          return `
        <span class="cart-drawer-custom__offers-progress-segment" style="--offers-segment-size:${segmentSize}; --offers-segment-fill:${segmentFill};">
          <span class="cart-drawer-custom__offers-progress-segment-fill"></span>
        </span>
      `;
        }).join('')
      : '';

    offerProgressNodes.forEach((offerProgress) => {
      const offerProgressEl = /** @type {HTMLElement} */ (offerProgress);
      const milestonesNode = offerProgressEl.querySelector('[data-gwp-offer-milestones]');
      const track = offerProgressEl.querySelector('[data-gwp-offer-progress-track]');
      if (!track) return;

      if (!offerThresholds.length) {
        track.innerHTML = '';
        offerProgressEl.style.setProperty('--offers-milestone-count', '1');
        return;
      }

      offerProgressEl.style.setProperty('--offers-milestone-count', `${segmentCount}`);
      if (milestonesNode instanceof HTMLElement) {
        milestonesNode.style.setProperty('--offers-milestone-count', `${segmentCount}`);
      }
      track.innerHTML = segmentsHtml;
    });
  }

  /** @param {{ items?: Array<{ variant_id?: number }> }} cart @param {number} subtotalCents */
  renderGwpRewards(cart, subtotalCents) {
    const offerThresholds = this.getThresholdsForNamespace('offer');
    const offerTwoThresholds = this.getThresholdsForNamespace('offer-two');
    const hasAnySubscription = this.hasAnySubscriptionInCart(cart);
    const shippingThreshold = offerThresholds.find((threshold) => `${threshold?.reward_type || ''}`.toLowerCase() === 'shipping') || null;
    const isShippingUnlockedBySubscription = Boolean(
      hasAnySubscription
      && shippingThreshold
      && subtotalCents < Number(shippingThreshold.amount || 0)
    );

    const offerTwoHasShipping = offerTwoThresholds.some((threshold) => `${threshold?.reward_type || ''}`.toLowerCase() === 'shipping');
    const fallbackShippingThresholds = offerTwoHasShipping
      ? []
      : offerThresholds.filter((threshold) => `${threshold?.reward_type || ''}`.toLowerCase() === 'shipping');

    const offerTwoStatusThresholds = [...offerTwoThresholds, ...fallbackShippingThresholds]
      .filter((threshold, index, arr) => arr.findIndex((item) => `${item?.key || ''}` === `${threshold?.key || ''}`) === index)
      .sort((a, b) => Number(a?.amount || 0) - Number(b?.amount || 0));

    const nextPendingOfferThreshold = this.getNextPendingThreshold(offerThresholds, cart, subtotalCents);
    const offerGiftThresholds = offerThresholds.filter((threshold) => threshold.reward_type === 'gift');
    const nextOfferGiftThreshold = offerGiftThresholds.find((threshold) => !this.isGiftThresholdInCart(cart, threshold)) || offerGiftThresholds[offerGiftThresholds.length - 1] || null;

    const offerTwoGiftThresholds = offerTwoThresholds.filter((threshold) => threshold.reward_type === 'gift');
    const nextOfferTwoGiftThreshold = offerTwoGiftThresholds.find((threshold) => !this.isGiftThresholdInCart(cart, threshold)) || offerTwoGiftThresholds[offerTwoGiftThresholds.length - 1] || null;
    const nextPendingOfferTwoGiftThreshold = this.getNextPendingGiftThreshold(offerTwoGiftThresholds, cart, subtotalCents);

    this.renderOfferTwoStatusList(cart, subtotalCents, offerTwoStatusThresholds, nextPendingOfferTwoGiftThreshold?.reward_name || '');

    // Update ALL offer toggle text instances (a mobile duplicate may exist).
    const offerToggleNodes = this.querySelectorAll('[data-gwp-offer-toggle-text]');
    const nextOfferToggleThreshold = nextPendingOfferThreshold;
    if (offerToggleNodes.length && nextOfferToggleThreshold) {
      let toggleHtml;
      let isInnerHtml = false;
      if (isShippingUnlockedBySubscription && shippingThreshold?.reward_name) {
        toggleHtml = `Unlocked ${shippingThreshold.reward_name}`;
      } else {
        const remaining = Math.max(0, Number(nextOfferToggleThreshold.amount || 0) - subtotalCents);
        const rewardName = `${nextOfferToggleThreshold.reward_name || 'your reward'}`;
        if (remaining > 0) {
          const remainingText = this.formatMoney(remaining).replace('.00', '');
          toggleHtml = `You are ${remainingText} away from unlocking <u>${rewardName}</u>`;
          isInnerHtml = true;
        } else {
          toggleHtml = `You unlocked ${rewardName}`;
        }
      }
      offerToggleNodes.forEach((node) => {
        if (isInnerHtml) {
          node.innerHTML = toggleHtml;
        } else {
          node.textContent = toggleHtml;
        }
      });
    }

    this.renderGwpRedeemState('offer', nextOfferGiftThreshold, subtotalCents, cart);
    this.renderGwpRedeemState('offer-two', nextOfferTwoGiftThreshold, subtotalCents, cart);
  }

  /** @param {{ items?: Array<any> }} cart @param {number} subtotalCents @param {Array<any>} thresholds @param {string} nextRewardName */
  renderOfferTwoStatusList(cart, subtotalCents, thresholds, nextRewardName = '') {
    const statusList = this.querySelector('[data-gwp-offer-two-status-list]');
    if (!statusList) return;

    if (!thresholds.length) {
      statusList.innerHTML = '';
      return;
    }

    const rowsHtml = thresholds.map((threshold) => {
      const isUnlocked = this.isThresholdUnlocked(cart, subtotalCents, threshold);
      const label = this.buildThresholdStatusText(subtotalCents, threshold, {
        nextRewardName,
        isUnlocked,
      });
      const icon = this.getStatusIconSvg(isUnlocked, threshold);
      return `
        <li class="cart-drawer-custom__offers-two-status-item">
          <span class="cart-drawer-custom__offers-two-status-icon" aria-hidden="true">${icon}</span>
          <span>${label}</span>
        </li>
      `;
    }).join('');

    statusList.innerHTML = rowsHtml;
  }

  /** @param {Array<any>} thresholds @param {{ items?: Array<any> }} cart @param {number} subtotalCents */
  getNextPendingThreshold(thresholds, cart, subtotalCents) {
    if (!thresholds.length) return null;

    const hasAnySubscription = this.hasAnySubscriptionInCart(cart);

    const nextLocked = thresholds.find((threshold) => {
      const thresholdAmount = Number(threshold?.amount || 0);
      const rewardType = `${threshold?.reward_type || ''}`.toLowerCase();

      if (rewardType === 'shipping' && hasAnySubscription) {
        return false;
      }

      if (subtotalCents < thresholdAmount) return true;

      if (threshold?.reward_type === 'gift') {
        return !this.isGiftThresholdInCart(cart, threshold);
      }

      return false;
    });

    return nextLocked || thresholds[thresholds.length - 1] || null;
  }

  /** @param {Array<any>} giftThresholds @param {{ items?: Array<any> }} cart @param {number} subtotalCents */
  getNextPendingGiftThreshold(giftThresholds, cart, subtotalCents) {
    if (!giftThresholds.length) return null;

    const nextLocked = giftThresholds.find((threshold) => {
      const thresholdAmount = Number(threshold?.amount || 0);
      if (subtotalCents < thresholdAmount) return true;
      return !this.isGiftThresholdInCart(cart, threshold);
    });

    return nextLocked || giftThresholds[giftThresholds.length - 1] || null;
  }

  /** @param {{ items?: Array<any> }} cart @param {number} subtotalCents @param {{ amount?: number; reward_type?: string; key?: string }} threshold */
  isThresholdUnlocked(cart, subtotalCents, threshold) {
    const rewardType = `${threshold?.reward_type || ''}`.toLowerCase();
    if (rewardType === 'shipping' && this.hasAnySubscriptionInCart(cart)) {
      return true;
    }

    const thresholdAmount = Number(threshold?.amount || 0);
    if (subtotalCents < thresholdAmount) return false;

    if (threshold?.reward_type !== 'gift') {
      return true;
    }

    return true;
  }

  /** @param {{ items?: Array<any> }} cart @param {{ key?: string }} threshold */
  isGiftThresholdInCart(cart, threshold) {
    const thresholdKey = `${threshold?.key || ''}`;
    if (!thresholdKey) return false;

    return (cart?.items || []).some((item) => `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}` === thresholdKey);
  }

  /** @param {boolean} isReached @param {{ reward_type?: string; icon_url?: string | null }} threshold */
  getStatusIconSvg(isReached, threshold = {}) {
    if (isReached) {
      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <ellipse cx="6" cy="6" rx="6" ry="5.5" fill="#D2AE67"/>
          <path d="M5.1225 7.14L3.66 5.6775L3 6.345L5.1225 8.4675L5.7825 7.8L9.3675 4.2225L8.7 3.5625L5.1225 7.14Z" fill="white"/>
          <path d="M6 0.00283512C4.4593 -0.0467774 2.96292 0.556426 1.83841 1.68041C0.713894 2.80439 0.0528597 4.35758 0 6C0.0528597 7.64242 0.713894 9.19561 1.83841 10.3196C2.96292 11.4436 4.4593 12.0468 6 11.9972C7.5407 12.0468 9.03708 11.4436 10.1616 10.3196C11.2861 9.19561 11.9471 7.64242 12 6C11.9471 4.35758 11.2861 2.80439 10.1616 1.68041C9.03708 0.556426 7.5407 -0.0467774 6 0.00283512ZM6 10.9976C4.70788 11.0474 3.44986 10.5496 2.50113 9.61299C1.5524 8.67642 0.9902 7.37737 0.9375 6C0.9902 4.62263 1.5524 3.32358 2.50113 2.38701C3.44986 1.45044 4.70788 0.952588 6 1.00236C7.29212 0.952588 8.55014 1.45044 9.49887 2.38701C10.4476 3.32358 11.0098 4.62263 11.0625 6C11.0098 7.37737 10.4476 8.67642 9.49887 9.61299C8.55014 10.5496 7.29212 11.0474 6 10.9976Z" fill="#D2AE67"/>
        </svg>
      `;
    }

    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="5" stroke="#BEB7A8" stroke-width="1.5" stroke-dasharray="1.5 1.5"/>
      </svg>
    `;
  }

  /** @param {'offer' | 'offer-two'} namespace @param {any | null} threshold @param {number} subtotalCents @param {{ items?: Array<any> }} cart */
  renderGwpRedeemState(namespace, threshold, subtotalCents, cart) {
    const nameNode = this.querySelector(namespace === 'offer' ? '[data-gwp-offer-reward-name]' : '[data-gwp-offer-two-reward-name]');
    const messageNode = this.querySelector(namespace === 'offer' ? '[data-gwp-offer-reward-message]' : '[data-gwp-offer-two-reward-message]');
    const rewardImage = /** @type {HTMLImageElement | null} */ (this.querySelector(namespace === 'offer' ? '[data-gwp-offer-reward-image]' : '[data-gwp-offer-two-reward-image]'));
    const redeemButton = /** @type {HTMLButtonElement | null} */ (this.querySelector(namespace === 'offer' ? '[data-gwp-offer-block] [data-gwp-redeem-button]' : '[data-gwp-offer-two-block] [data-gwp-redeem-button]'));
    const redeemLabel = redeemButton?.querySelector('[data-gwp-redeem-button-label]');

    if (rewardImage) {
      if (!rewardImage.dataset.defaultSrc) {
        rewardImage.dataset.defaultSrc = rewardImage.getAttribute('src') || '';
        rewardImage.dataset.defaultAlt = rewardImage.getAttribute('alt') || '';
      }

      if (threshold?.reward_image_url) {
        rewardImage.src = threshold.reward_image_url;
        rewardImage.alt = `${threshold?.reward_name || rewardImage.dataset.defaultAlt || 'Reward image'}`;
      } else {
        rewardImage.src = rewardImage.dataset.defaultSrc || rewardImage.src;
        rewardImage.alt = rewardImage.dataset.defaultAlt || rewardImage.alt;
      }
    }

    if (!threshold) {
      if (redeemButton) redeemButton.disabled = true;
      return;
    }

    const thresholdAmount = Number(threshold.amount || 0);
    const giftVariantId = Number(threshold.gift_variant_id || 0);
    const hasGiftInCart = this.isGiftThresholdInCart(cart, threshold);
    const thresholdReached = subtotalCents >= thresholdAmount && !hasGiftInCart;
    const isEligible = thresholdReached && giftVariantId > 0;
    const remaining = Math.max(0, thresholdAmount - subtotalCents);

    if (nameNode && threshold.reward_name) {
      nameNode.textContent = threshold.reward_name;
    }

    if (messageNode) {
      const rewardDescription = `${threshold?.reward_description || ''}`.trim();
      messageNode.textContent = rewardDescription || this.buildThresholdStatusText(subtotalCents, threshold, {
        nextRewardName: `${threshold?.reward_name || ''}`,
      });
    }

    if (redeemButton) {
      redeemButton.disabled = !thresholdReached;
      redeemButton.dataset.gwpVariantId = giftVariantId > 0 ? `${giftVariantId}` : '';
      redeemButton.dataset.gwpThresholdKey = `${threshold.key || ''}`;
    }

    if (redeemLabel) {
      if (hasGiftInCart) {
        redeemLabel.textContent = 'REDEEMED';
      } else if (thresholdReached) {
        redeemLabel.textContent = 'REDEEM OFFER';
      } else if (remaining > 0) {
        const messageTemplate = `${threshold.message || 'SPEND $[amount] MORE TO REDEEM OFFER'}`;
        const amountText = this.formatMoney(remaining).replace('.00', '');
        redeemLabel.textContent = messageTemplate.replace('$[amount]', amountText);
      } else {
        redeemLabel.textContent = 'REDEEM OFFER';
      }
    }

    if (thresholdReached) {
      const toggleNamespace = namespace === 'offer' ? 'offers' : 'offers-two';
      const toggleButton = /** @type {HTMLButtonElement | null} */ (this.querySelector(`.cart-drawer-custom__${toggleNamespace}-toggle`));
      const togglePanel = /** @type {HTMLElement | null} */ (this.querySelector(`.cart-drawer-custom__${toggleNamespace}-panel`));
      const toggleLabel = /** @type {HTMLElement | null} */ (this.querySelector(`.cart-drawer-custom__${toggleNamespace}-toggle-label`));

      if (toggleButton && togglePanel && toggleLabel) {
        toggleButton.setAttribute('aria-expanded', 'true');
        toggleLabel.textContent = 'CLOSE';
        if (toggleNamespace === 'offers') {
          togglePanel.classList.add('show');
        } else {
          togglePanel.classList.remove('is-collapsed');
        }
      }
    }
  }

  /** @param {number} subtotalCents @param {{ amount?: number; reward_name?: string; reward_type?: string; message?: string }} threshold @param {{ nextRewardName?: string; isUnlocked?: boolean }} options */
  buildThresholdStatusText(subtotalCents, threshold, options = {}) {
    const thresholdAmount = Number(threshold?.amount || 0);
    const remaining = Math.max(0, thresholdAmount - subtotalCents);
    const fallbackRewardName = `${threshold?.reward_name || 'reward'}`;
    const nextRewardName = `${options?.nextRewardName || ''}`.trim();
    const rewardType = `${threshold?.reward_type || ''}`.toLowerCase();
    const rewardName = rewardType === 'shipping'
      ? fallbackRewardName
      : (rewardType === 'gift' ? fallbackRewardName : (nextRewardName || fallbackRewardName));

    if (options?.isUnlocked || remaining <= 0) {
      return `Unlocked ${rewardName}`;
    }

    const amountText = this.formatMoney(remaining).replace('.00', '');
    const messageTemplate = `${threshold?.message || 'You are $[amount] away from [reward]'}`;

    return messageTemplate
      .replaceAll('$[amount]', amountText)
      .replaceAll('[amount]', amountText)
      .replaceAll('[next_reward]', rewardName)
      .replaceAll('[next_gift]', rewardName)
      .replaceAll('[reward]', rewardName);
  }

  /** @param {{ items?: Array<any> } | null} cart */
  hasAnySubscriptionInCart(cart) {
    return (cart?.items || []).some((item) => Boolean(item?.selling_plan_allocation));
  }

  /** @param {{ items?: Array<any> } | null} cart */
  hasClaimOffSubscriptionInCart(cart) {
    return (cart?.items || []).some((item) => {
      const allocation = item?.selling_plan_allocation;
      if (!allocation) return false;

      const planId = Number(
        allocation?.selling_plan?.id
        || allocation?.selling_plan_id
        || 0
      );

      return Number.isFinite(planId) && planId === SUBSCRIPTION_UPGRADE_SELLING_PLAN_ID;
    });
  }

  /** @param {{ items?: Array<{ variant_id?: number }> }} cart */
  renderGwpUpsell(cart) {
    const upsellBlock = this.querySelector('[data-gwp-upsell-block]');
    if (!upsellBlock) return;

    if (!this.gwpUpsells.length) {
      upsellBlock.style.display = 'none';
      return;
    }

    const hasClaimOffSubscriptionInCart = this.hasClaimOffSubscriptionInCart(cart);

    if (!hasClaimOffSubscriptionInCart) {
      upsellBlock.style.display = 'none';
      return;
    }

    const variantIdsInCart = new Set(
      (cart?.items || [])
        .map((item) => Number(item?.variant_id || 0))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    const nextUpsell = this.gwpUpsells.find((upsell) => {
      const variantId = Number(upsell?.id || 0);
      return variantId > 0 && !variantIdsInCart.has(variantId);
    }) || null;

    if (!nextUpsell) {
      upsellBlock.style.display = 'none';
      return;
    }

    upsellBlock.style.display = '';

    const image = /** @type {HTMLImageElement | null} */ (upsellBlock.querySelector('[data-gwp-upsell-image]'));
    const title = upsellBlock.querySelector('[data-gwp-upsell-title]');
    const addButton = /** @type {HTMLButtonElement | null} */ (upsellBlock.querySelector('[data-gwp-upsell-add]'));
    const addLabel = upsellBlock.querySelector('[data-gwp-upsell-add-label]');

    if (image && nextUpsell.image_url) {
      image.src = nextUpsell.image_url;
      image.alt = nextUpsell.title || image.alt;
    }

    if (title) {
      title.textContent = nextUpsell.description || nextUpsell.title || '';
    }

    if (addButton) {
      addButton.dataset.gwpUpsellVariantId = `${Number(nextUpsell.id || 0)}`;
      addButton.disabled = false;
    }

    if (addLabel) {
      const priceText = this.formatMoney(Number(nextUpsell.price || 0)).replace('.00', '');
      addLabel.textContent = `ADD | ${priceText}`;
    }
  }

  /** @param {Event} event */
  async handleGwpRedeemClick(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-gwp-redeem-button]'));
    if (!button || button.disabled) return;
    const offerCard = /** @type {HTMLElement | null} */ (button.closest('.cart-drawer-custom__offers-exclusive, .cart-drawer-custom__offers-two-exclusive'));

    const variantId = Number(button.dataset.gwpVariantId || 0);
    const thresholdKey = `${button.dataset.gwpThresholdKey || ''}`;
    if (!variantId) return;

    if (offerCard?.classList.contains('cart-drawer-custom__loading-card--loading')) return;
    this.setCardLoadingState(offerCard, true);
    button.disabled = true;

    try {
      // Guard: skip add if this gift is already in the cart to prevent duplicates.
      const currentCart = await this.fetchCartState();
      const effectiveKey = thresholdKey || `manual-${variantId}`;
      const alreadyInCart = (currentCart?.items || []).some(
        (item) => `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}` === effectiveKey
      );
      if (alreadyInCart) {
        await this.refreshDrawerContent(currentCart);
        return;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                [GWP_THRESHOLD_KEY_PROPERTY]: effectiveKey,
                [GWP_REWARD_TYPE_PROPERTY]: 'gift',
                [GWP_LOCKED_PROPERTY]: 'true',
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to redeem reward');
      }

      await this.refreshDrawerContent();
    } catch (error) {
      console.error('Error redeeming GWP reward:', error);
    } finally {
      button.disabled = false;
      if (offerCard?.isConnected) {
        this.setCardLoadingState(offerCard, false);
      }
    }
  }

  /** @param {Event} event */
  async handleGwpUpsellAddClick(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-gwp-upsell-add]'));
    if (!button || button.disabled) return;
    const upsellCard = /** @type {HTMLElement | null} */ (button.closest('.cart-drawer-custom__inline-offer'));

    const variantId = Number(button.dataset.gwpUpsellVariantId || 0);
    if (!variantId) return;

    if (upsellCard?.classList.contains('cart-drawer-custom__loading-card--loading')) return;
    this.setCardLoadingState(upsellCard, true);
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add GWP upsell product');
      }

      const cart = await this.fetchCartState();
      await this.refreshDrawerContent(cart);
    } catch (error) {
      console.error('Error adding GWP upsell product:', error);
    } finally {
      button.disabled = false;
      if (upsellCard?.isConnected) {
        this.setCardLoadingState(upsellCard, false);
      }
    }
  }

  /** @param {{ items?: Array<{ variant_id?: number; quantity?: number }> }} cart */
  getCartCrossSellSavingsCents(cart) {
    if (!cart?.items?.length || !this.crossSellRules.length) return 0;

    const rulesSortedByPriority = [...this.crossSellRules].sort((a, b) => {
      const aPriority = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : 9999;
      const bPriority = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : 9999;
      return aPriority - bPriority;
    });

    const savingsByVariantId = new Map();
    rulesSortedByPriority.forEach((rule) => {
      const variantId = Number(rule?.bundle_variant_id || 0);
      if (!variantId || savingsByVariantId.has(variantId)) return;

      const bundleOtpCents = Number(rule?.bundle_otp_price || 0);
      const bundleSubCents = Number(rule?.bundle_price || 0);
      const perUnitSavingsCents = Math.max(0, bundleOtpCents - bundleSubCents);
      savingsByVariantId.set(variantId, perUnitSavingsCents);
    });

    return cart.items.reduce((total, item) => {
      const variantId = Number(item?.variant_id || 0);
      const quantity = Number(item?.quantity || 0);
      const perUnitSavingsCents = savingsByVariantId.get(variantId) || 0;
      if (!perUnitSavingsCents || quantity <= 0) return total;
      return total + (perUnitSavingsCents * quantity);
    }, 0);
  }

  /** @param {{ items?: Array<{ product_id?: number; variant_id?: number }> }} cart */
  getActiveCrossSellRule(cart) {
    if (!cart?.items?.length) return null;

    const productIdsInCart = new Set(
      cart.items
        .map((item) => Number(item?.product_id || 0))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    const variantIdsInCart = new Set(
      cart.items
        .map((item) => Number(item?.variant_id || 0))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    const matchingRules = this.crossSellRules
      .filter((rule) => {
        const triggerIds = Array.isArray(rule?.trigger_ids)
          ? [...new Set(rule.trigger_ids.map((id) => Number(id || 0)).filter((id) => Number.isFinite(id) && id > 0))]
          : [];

        // Rule only matches when all trigger products are present in cart.
        const isTriggered = triggerIds.length > 0 && triggerIds.every((id) => productIdsInCart.has(id));
        if (!isTriggered) return false;

        // If suggested cross-sell variant is already in cart, skip this rule and allow next one.
        const suggestedVariantId = Number(rule?.bundle_variant_id || 0);
        if (suggestedVariantId > 0 && variantIdsInCart.has(suggestedVariantId)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        const aTriggerCount = Array.isArray(a?.trigger_ids)
          ? new Set(a.trigger_ids.map((id) => Number(id || 0)).filter((id) => Number.isFinite(id) && id > 0)).size
          : 0;
        const bTriggerCount = Array.isArray(b?.trigger_ids)
          ? new Set(b.trigger_ids.map((id) => Number(id || 0)).filter((id) => Number.isFinite(id) && id > 0)).size
          : 0;

        // Prefer most specific rule first (more trigger products).
        if (aTriggerCount !== bTriggerCount) {
          return bTriggerCount - aTriggerCount;
        }

        const aPriority = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : 9999;
        const bPriority = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : 9999;
        return aPriority - bPriority;
      });

    return matchingRules[0] || null;
  }

  /**
   * @param {any | null} rule
   * @param {{ items?: Array<any> } | null} [cart]
   */
  renderCrossSellTile(rule, cart = null) {
    const container = this.querySelector('.cart-drawer-custom__bundle-offer-container');
    if (!container) return;

    if (!rule) {
      container.style.display = 'none';
      return;
    }

    container.style.display = '';

    const kicker = container.querySelector('.cart-drawer-custom__bundle-offer-kicker');
    const title = container.querySelector('.cart-drawer-custom__bundle-offer-title');
    const description = container.querySelector('.cart-drawer-custom__bundle-offer-description');
    const priceUnit = container.querySelector('.cart-drawer-custom__bundle-offer-price-unit');
    const priceValue = container.querySelector('.cart-drawer-custom__bundle-offer-price-value');
    const badge = container.querySelector('.cart-drawer-custom__bundle-offer-badge span:last-child');
    const action = container.querySelector('.cart-drawer-custom__bundle-offer-action span');
    const image = /** @type {HTMLImageElement | null} */ (container.querySelector('.cart-drawer-custom__bundle-offer-image'));

    const ruleLabel = rule.label || '';
    const promoFactor = rule.promo_enabled && Number(rule.promo_discount_percent || 0) > 0
      ? (100 - Number(rule.promo_discount_percent)) / 100
      : 1;
    const bundleOtpCents = Number(rule.bundle_otp_price || 0);
    const bundleSubCents = Number(rule.bundle_price || 0);
    const bundleOtpPromoCents = promoFactor < 1 ? Number(rule.bundle_otp_price_promo || 0) || Math.round(bundleOtpCents * promoFactor) : bundleOtpCents;
    const bundleSubPromoCents = promoFactor < 1 ? Number(rule.bundle_price_promo || 0) || Math.round(bundleSubCents * promoFactor) : bundleSubCents;
    const bundleSavingsCents = Math.max(0, bundleOtpPromoCents - bundleSubPromoCents);
    const savingsDisplay = this.formatMoney(bundleSavingsCents).replace('.00', '');

    // Use the price from the first trigger item in cart (subscription line preferred over OTP).
    let includedTotalDisplay = '';
    if (cart?.items?.length && Array.isArray(rule?.trigger_ids)) {
      const triggerProductIds = new Set(
        rule.trigger_ids.map((id) => Number(id || 0)).filter((id) => Number.isFinite(id) && id > 0)
      );
      const firstTriggerItem = cart.items
        .slice()
        .sort((a, b) => (Boolean(a?.selling_plan_allocation) ? 0 : 1) - (Boolean(b?.selling_plan_allocation) ? 0 : 1))
        .find((item) => triggerProductIds.has(Number(item?.product_id || 0)));
      if (firstTriggerItem) {
        const triggerUnitCents = Math.round(Number(firstTriggerItem.original_price || firstTriggerItem.price || 0) * promoFactor);
        if (triggerUnitCents > 0) {
          includedTotalDisplay = this.formatMoney(triggerUnitCents + bundleOtpPromoCents).replace('.00', '');
        }
      }
    }
    if (!includedTotalDisplay) {
      includedTotalDisplay = typeof rule.included_total_price_display === 'string' && rule.included_total_price_display.trim()
        ? this.formatMoney(Math.round(Number(rule.included_total_price_cents || 0) * promoFactor)).replace('.00', '') || rule.included_total_price_display
        : (typeof rule.bundle_price_display === 'string' ? this.formatMoney(bundleOtpPromoCents).replace('.00', '') : '');
    }
    const subscriptionDisplay = this.formatMoney(bundleSubPromoCents).replace('.00', '');

    if (kicker && typeof rule.top_label === 'string' && rule.top_label.trim()) {
      kicker.textContent = rule.top_label;
    }
    if (title && ruleLabel) {
      title.textContent = ruleLabel;
    }
    if (description && typeof rule.description === 'string' && rule.description.trim()) {
      description.textContent = rule.description;
    }
    if (priceUnit && includedTotalDisplay) {
      priceUnit.textContent = includedTotalDisplay;
    }
    if (priceValue && subscriptionDisplay) {
      priceValue.textContent = subscriptionDisplay;
    }
    if (badge) {
      const badgeTemplate = badge.textContent || 'Save $x when you bundle';
      badge.textContent = badgeTemplate.replace('$x', savingsDisplay);
    }
    if (action) {
      const actionTemplate = action.textContent || 'UPGRADE & SAVE $XX';
      action.textContent = actionTemplate.replace('$XX', savingsDisplay);
    }
    if (image) {
      image.src = rule.image_url || CROSS_SELL_FALLBACK_IMAGE;
      image.alt = ruleLabel || 'Cross sell product';
    }
  }

  /**
   * Bundle upsell nudge ("SAVE ADDITIONAL 10% WHEN YOU BUNDLE"). The banner renders
   * from the section's customizer settings ([data-cart-bundle-upsell]); this toggles
   * it per cart state: visible while the cart has 1..(min-1) eligible items, hidden
   * when empty, at/over the threshold, or when the cart already carries the
   * bundle-tier discount (e.g. a lone 2-pack gets the top tier natively).
   * @param {{ items?: Array<any> } | null} cart
   */
  updateBundleUpsellMessage(cart) {
    const banners = this.querySelectorAll('[data-cart-bundle-upsell]');
    if (!banners.length) return;

    // Tied to the sitewide promo switch: the banner only ever shows while the
    // Site Promotion metaobject is Active, so the customizer toggle can stay ON
    // year-round and the message appears/disappears with the sale itself.
    if (!window.__promoConfig?.enabled) {
      banners.forEach((banner) => { banner.hidden = true; });
      return;
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    let eligibleQty = 0;
    let eligibleOriginalCents = 0;
    let discountAllocatedCents = 0;

    items.forEach((item) => {
      const thresholdKey = `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}`;
      const rewardType = `${item?.properties?.[GWP_REWARD_TYPE_PROPERTY] || ''}`.toLowerCase();
      if (thresholdKey !== '' || rewardType === 'gift') return;

      eligibleQty += Number(item?.quantity || 0);
      eligibleOriginalCents += Number(item?.original_line_price || 0);
      const allocations = Array.isArray(item?.discount_allocations) ? item.discount_allocations : [];
      discountAllocatedCents += allocations.reduce((sum, alloc) => sum + Number(alloc?.amount || 0), 0);
    });

    const promoConfig = /** @type {{ discountPercent?: number, tier2Percent?: number } | undefined} */ (window.__promoConfig);
    const basePct = Number(promoConfig?.discountPercent || 0);
    const tier2Pct = Number(promoConfig?.tier2Percent || 0);
    const appliedPct = eligibleOriginalCents > 0 ? (discountAllocatedCents / eligibleOriginalCents) * 100 : 0;
    // Already at the bundle tier when the applied discount is past the midpoint
    // between tiers (guards the lone-2-pack cart, which is top-tier at qty 1).
    const atBundleTier = tier2Pct > basePct && appliedPct >= (basePct + tier2Pct) / 2;

    banners.forEach((banner) => {
      const minItems = Number(banner.getAttribute('data-bundle-upsell-min-items') || 2);
      const show = eligibleQty > 0 && eligibleQty < minItems && !atBundleTier;
      banner.hidden = !show;
    });
  }

  /** @param {{ items?: Array<any>; items_subtotal_price?: number; total_price?: number } | null} cart */
  updateSummaryDynamic(cart) {
    this.updateBundleUpsellMessage(cart);

    const summaryPanel = this.querySelector('.cart-drawer-custom__summary-breakdown-panel');
    if (!summaryPanel) return;

    const totalAmountNode = this.querySelector('.cart-drawer-custom__summary-total--amount');
    if (totalAmountNode) {
      const displayTotalCents = this.getDisplayTotalCents(cart);
      totalAmountNode.textContent = this.formatMoney(displayTotalCents);
    }

    const dynamicRows = summaryPanel.querySelectorAll('.cart-drawer-custom__summary-breakdown-row--dynamic');
    dynamicRows.forEach((row) => row.remove());

    const subtotalCents = this.getThresholdSubtotalCents(cart);
    const hasAnySubscription = this.hasAnySubscriptionInCart(cart);
    const shippingThreshold = this.getAllGwpThresholds().find((threshold) => threshold.reward_type === 'shipping') || null;
    const shippingThresholdReached = shippingThreshold
      ? (subtotalCents >= Number(shippingThreshold.amount || 0) || hasAnySubscription)
      : hasAnySubscription;

    const subscriptionRow = /** @type {HTMLElement | null} */ (
      summaryPanel.querySelector('.cart-drawer-custom__summary-breakdown-row--subscription')
      || Array.from(summaryPanel.querySelectorAll('.cart-drawer-custom__summary-breakdown-row')).find((row) => {
        const label = row.querySelector('.cart-drawer-custom__summary-breakdown-label');
        return (label?.textContent || '').trim().toLowerCase() === 'subscription savings';
      })
    );

    const shippingRow = /** @type {HTMLElement | null} */ (
      summaryPanel.querySelector('.cart-drawer-custom__summary-breakdown-row--shipping')
      || Array.from(summaryPanel.querySelectorAll('.cart-drawer-custom__summary-breakdown-row')).find((row) => {
        const label = row.querySelector('.cart-drawer-custom__summary-breakdown-label');
        const normalized = (label?.textContent || '').trim().toLowerCase();
        return normalized === 'free shipping' || normalized === 'complimentary shipping';
      })
    );

    const legacyRewardRow = /** @type {HTMLElement | null} */ (summaryPanel.querySelector('.cart-drawer-custom__summary-breakdown-row--reward-legacy'));
    const legacyShippingRow = /** @type {HTMLElement | null} */ (summaryPanel.querySelector('.cart-drawer-custom__summary-breakdown-row--shipping-legacy'));

    if (legacyRewardRow) {
      legacyRewardRow.classList.add('is-hidden');
      legacyRewardRow.style.display = 'none';
    }

    if (legacyShippingRow) {
      legacyShippingRow.classList.add('is-hidden');
      legacyShippingRow.style.display = 'none';
    }

    let shippingSavingsCents = 0;
    if (shippingRow) {
      const shippingLabelNode = shippingRow.querySelector('.cart-drawer-custom__summary-breakdown-label');
      const shippingValueNode = shippingRow.querySelector('.cart-drawer-custom__summary-breakdown-value');
      const thresholdShippingSavings = Number(shippingThreshold?.shipping_savings_cents || 0);
      const shippingRewardName = `${shippingThreshold?.reward_name || ''}`.trim() || 'Complimentary shipping';

      if (shippingLabelNode) {
        shippingLabelNode.textContent = shippingRewardName;
      }

      shippingSavingsCents = thresholdShippingSavings > 0
        ? thresholdShippingSavings
        : Math.abs(this.parseMoneyTextToCents(shippingValueNode?.textContent || '0'));
      shippingRow.classList.toggle('is-hidden', !shippingThresholdReached);
      shippingRow.style.display = shippingThresholdReached ? '' : 'none';

      if (shippingValueNode && shippingThresholdReached && shippingSavingsCents > 0) {
        const formattedShippingSavings = this.formatMoney(shippingSavingsCents).replace('.00', '');
        shippingValueNode.textContent = `-${formattedShippingSavings}`;
      }
    }

    // Build a set of variant IDs for bundle products (data-product-type="bundle").
    // We use variant_id (not line key) because Shopify changes line keys when a selling plan is applied.
    const bundleVariantIds = new Set();
    const domCompareAtByLineKey = new Map();
    const domCompareAtByVariantId = new Map();
    this.querySelectorAll('.cart-drawer-custom__item[data-product-type="bundle"]').forEach((itemCard) => {
      const variantId = Number(/** @type {HTMLElement} */ (itemCard).dataset.variantId || 0);
      if (variantId > 0) bundleVariantIds.add(variantId);
    });

    this.querySelectorAll('.cart-drawer-custom__item').forEach((itemCard) => {
      const cardEl = /** @type {HTMLElement} */ (itemCard);
      const lineItemKey = `${cardEl.dataset.lineItemKey || ''}`;
      const variantId = Number(cardEl.dataset.variantId || 0);
      const domCompareAtCents = Number(cardEl.dataset.variantCompareAtCents || cardEl.dataset.retailUnitCents || 0);

      if (domCompareAtCents > 0) {
        if (lineItemKey) {
          domCompareAtByLineKey.set(lineItemKey, domCompareAtCents);
        }
        if (variantId > 0 && !domCompareAtByVariantId.has(variantId)) {
          domCompareAtByVariantId.set(variantId, domCompareAtCents);
        }
      }
    });

    const getSubscriptionBaselineUnitCents = (item) => {
      const lineItemKey = `${item?.key || ''}`;
      const variantId = Number(item?.variant_id || 0);
      const domCompareAtUnitCents = Number(
        domCompareAtByLineKey.get(lineItemKey)
        || domCompareAtByVariantId.get(variantId)
        || 0
      );

      const compareAtCandidates = [
        domCompareAtUnitCents,
        Number(item?.variant?.compare_at_price || 0),
        Number(item?.compare_at_price || 0),
        Number(item?.selling_plan_allocation?.compare_at_price || 0),
      ].filter((value) => Number.isFinite(value) && value > 0);

      if (compareAtCandidates.length) {
        return Math.max(...compareAtCandidates);
      }

      const originalUnitCents = Number(item?.original_price || item?.price || 0);
      return Number.isFinite(originalUnitCents) && originalUnitCents > 0 ? originalUnitCents : 0;
    };

    const debugLineItems = [];

    const subscriptionSavingsCents = (cart?.items || []).reduce((total, item) => {
      if (!item?.selling_plan_allocation) return total;

      const variantId = Number(item?.variant_id || 0);
      const quantity = Number(item?.quantity || 0);
      const finalLineCents = Number(item?.final_line_price ?? item?.line_price ?? 0);
      const baselineUnitCents = getSubscriptionBaselineUnitCents(item);
      const baselineLineCents = Math.max(0, baselineUnitCents * Math.max(0, quantity));
      const lineSavingsCents = Math.max(0, baselineLineCents - (Number.isFinite(finalLineCents) ? finalLineCents : 0));

      if (this.isSubscriptionSavingsDebugEnabled()) {
        const lineItemKey = `${item?.key || ''}`;
        const domCompareAtUnitCents = Number(
          domCompareAtByLineKey.get(lineItemKey)
          || domCompareAtByVariantId.get(variantId)
          || 0
        );

        debugLineItems.push({
          key: item?.key || '',
          title: item?.product_title || item?.title || '',
          variantId,
          quantity,
          isBundle: bundleVariantIds.has(variantId),
          compareAtDom: domCompareAtUnitCents,
          compareAtVariant: Number(item?.variant?.compare_at_price || 0),
          compareAtItem: Number(item?.compare_at_price || 0),
          compareAtAllocation: Number(item?.selling_plan_allocation?.compare_at_price || 0),
          originalUnit: Number(item?.original_price || item?.price || 0),
          baselineUnit: baselineUnitCents,
          baselineLine: baselineLineCents,
          finalLine: finalLineCents,
          lineSavings: lineSavingsCents,
        });
      }

      if (bundleVariantIds.has(variantId)) {
        // Bundle: savings = compare_at_price → final subscription price
        return total + lineSavingsCents;
      }

      // Regular product: savings = compare_at_price (or original) -> final subscription price.
      if (!Number.isFinite(finalLineCents) || finalLineCents < 0 || quantity <= 0) return total;
      return total + lineSavingsCents;
    }, 0);

    this.logSubscriptionSavingsDebug({
      lineItems: debugLineItems,
      totalSavingsCents: subscriptionSavingsCents,
    });

    const showSubscriptionSavings = subscriptionSavingsCents > 0;

    if (subscriptionRow) {
      subscriptionRow.classList.toggle('is-hidden', !showSubscriptionSavings);
      subscriptionRow.style.display = showSubscriptionSavings ? '' : 'none';
      const subscriptionValue = subscriptionRow.querySelector('.cart-drawer-custom__summary-breakdown-value');
      if (subscriptionValue && showSubscriptionSavings) {
        const formatted = this.formatMoney(subscriptionSavingsCents).replace('.00', '');
        subscriptionValue.textContent = `-${formatted}`;
      }
    }

    // Promo / automatic discount row (e.g. Memorial Day Sale)
    const promoRow = /** @type {HTMLElement | null} */ (summaryPanel.querySelector('[data-cart-promo-savings-row]'));
    if (promoRow) {
      const promoConfig = /** @type {{ enabled?: boolean, discountPercent?: number } | undefined} */ (window.__promoConfig);
      const promoEnabled = Boolean(promoConfig?.enabled);
      let promoSavingsCents = 0;
      let promoLabel = 'Sale discount';

      if (promoEnabled) {
        // Prefer real discount_applications data when available; fall back to manual calc.
        const discountApps = Array.isArray(cart?.discount_applications) ? cart.discount_applications : [];
        const autoDiscount = discountApps.find((d) => d?.type === 'automatic' || d?.type === 'discount_code');
        if (autoDiscount?.title) promoLabel = autoDiscount.title;

        if (discountApps.length > 0) {
          promoSavingsCents = (cart?.items || []).reduce((total, item) => {
            const allocations = Array.isArray(item?.discount_allocations) ? item.discount_allocations : [];
            return total + allocations.reduce((sum, alloc) => sum + Number(alloc?.amount || 0), 0);
          }, 0);
        } else {
          // Fallback: calculate from promo percent on original prices.
          const pct = Number(promoConfig?.discountPercent || 0);
          promoSavingsCents = pct > 0
            ? (cart?.items || []).reduce((total, item) => {
              if (item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY]) return total;
              const originalLine = Number(item?.original_line_price || 0);
              return total + Math.round(originalLine * pct / 100);
            }, 0)
            : 0;
        }
      }

      const showPromoSavings = promoEnabled && promoSavingsCents > 0;
      promoRow.classList.toggle('is-hidden', !showPromoSavings);
      promoRow.style.display = showPromoSavings ? '' : 'none';
      const promoLabelEl = promoRow.querySelector('[data-cart-promo-savings-label]');
      const promoValueEl = promoRow.querySelector('[data-cart-promo-savings-value]');
      if (promoLabelEl) promoLabelEl.textContent = promoLabel;
      if (promoValueEl && showPromoSavings) {
        promoValueEl.textContent = `-${this.formatMoney(promoSavingsCents).replace('.00', '')}`;
      }
    }

    // Build-your-own packs (ELM Bundle Builder app): one row per pack with the discount its
    // shade lines carry. Subscription lines are already in the subscription row.
    const packSavingsByTitle = new Map();
    (cart?.items || []).forEach((item) => {
      const props = item?.properties || {};
      const isPackLine = Boolean(props._bundle_id)
        && !props._bundle_original_total
        && (Boolean(props._bundle_title) || (Boolean(props._bundle_product) && !props._byob_bundle));
      if (!isPackLine || item?.selling_plan_allocation) return;
      const lineSavingsCents = Number(item?.original_line_price || 0) - Number(item?.final_line_price || 0);
      if (!(lineSavingsCents > 0)) return;
      let title = `${props._bundle_title || ''}`.trim() || 'Bundle savings';
      // Pack titles are often stored in capitals; the other rows read in title case.
      if (title === title.toUpperCase()) {
        title = title.toLowerCase().replace(/(^|[\s-])(\S)/g, (match) => match.toUpperCase());
      }
      packSavingsByTitle.set(title, (packSavingsByTitle.get(title) || 0) + lineSavingsCents);
    });

    packSavingsByTitle.forEach((packSavingsCents, title) => {
      const row = document.createElement('div');
      row.className = 'cart-drawer-custom__summary-breakdown-row cart-drawer-custom__summary-breakdown-row--dynamic cart-drawer-custom__summary-breakdown-row--pack';

      const labelNode = document.createElement('span');
      labelNode.className = 'cart-drawer-custom__summary-breakdown-label';
      labelNode.textContent = title;

      const valueNode = document.createElement('span');
      valueNode.className = 'cart-drawer-custom__summary-breakdown-value';
      valueNode.textContent = `-${this.formatMoney(packSavingsCents).replace('.00', '')}`;

      row.appendChild(labelNode);
      row.appendChild(valueNode);
      const panelContent = summaryPanel.querySelector('.cart-drawer-custom__summary-breakdown-panel-content') || summaryPanel;
      panelContent.appendChild(row);
    });

    const redeemGiftLineItems = (cart?.items || []).filter((item) => {
      const thresholdKey = `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}`;
      const rewardType = `${item?.properties?.[GWP_REWARD_TYPE_PROPERTY] || ''}`.toLowerCase();
      return thresholdKey !== '' || rewardType === 'gift';
    });

    const allThresholds = this.getAllGwpThresholds();

    // A gift line names the threshold block that added it, but that id is the drawer's or the
    // cart page's block, and a dated tier taking over (BFCM stages) changes it too: match the
    // id first, then the gift variant, as the cart templates do for the gift's value.
    const findThresholdForGiftLine = (item) => {
      const thresholdKey = `${item?.properties?.[GWP_THRESHOLD_KEY_PROPERTY] || ''}`;
      const itemVariantId = Number(item?.variant_id || 0);
      return (thresholdKey && allThresholds.find((threshold) => `${threshold?.key || ''}` === thresholdKey))
        || (itemVariantId > 0 && allThresholds.find((threshold) => Number(threshold?.gift_variant_id || 0) === itemVariantId))
        || null;
    };

    const getRewardLabelForGiftLine = (item, index) => {
      const matchedThreshold = findThresholdForGiftLine(item);

      const schemaRewardName = `${matchedThreshold?.reward_name || ''}`.trim();
      if (schemaRewardName) {
        return schemaRewardName;
      }

      return `${item?.product_title || item?.title || `Complimentary item ${index + 1}`}`;
    };

    redeemGiftLineItems.forEach((item, index) => {
      const quantity = Number(item?.quantity || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) return;

      const matchedThreshold = findThresholdForGiftLine(item);

      const overrideValueCents = Number(matchedThreshold?.gift_display_value_cents || 0);
      let otpLineCents;
      if (overrideValueCents > 0) {
        otpLineCents = overrideValueCents * quantity;
      } else {
        const originalUnitCents = Number(item?.original_price || 0);
        const fallbackUnitCents = Number(item?.price || 0);
        const compareAtUnitCents = Number(item?.variant?.compare_at_price || 0);
        const otpUnitCents = Math.max(0, compareAtUnitCents || originalUnitCents || fallbackUnitCents);
        otpLineCents = otpUnitCents * quantity;
      }

      if (!otpLineCents) return;

      const row = document.createElement('div');
      row.className = 'cart-drawer-custom__summary-breakdown-row cart-drawer-custom__summary-breakdown-row--dynamic';

      const labelNode = document.createElement('span');
      labelNode.className = 'cart-drawer-custom__summary-breakdown-label';
      labelNode.textContent = getRewardLabelForGiftLine(item, index);

      const valueNode = document.createElement('span');
      valueNode.className = 'cart-drawer-custom__summary-breakdown-value';
      const formattedOtpLine = this.formatMoney(otpLineCents).replace('.00', '');
      valueNode.textContent = `-${formattedOtpLine}`;

      row.appendChild(labelNode);
      row.appendChild(valueNode);
      const panelContent = summaryPanel.querySelector('.cart-drawer-custom__summary-breakdown-panel-content') || summaryPanel;
      panelContent.appendChild(row);
    });

    this.updateSummaryBreakdownTitle();
  }

  updateSummaryBreakdownTitle() {
    const summaryPanel = this.querySelector('.cart-drawer-custom__summary-breakdown-panel');
    const title = this.querySelector('.cart-drawer-custom__summary-breakdown-title');
    if (!summaryPanel || !title) return;

    const allRows = summaryPanel.querySelectorAll('.cart-drawer-custom__summary-breakdown-row');
    const visibleRows = Array.from(allRows).filter((row) => {
      return row.style.display !== 'none' && !row.classList.contains('is-hidden');
    });

    const toggle = this.querySelector('.cart-drawer-custom__summary-breakdown-toggle');
    const summaryBreakdown = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer-custom__summary-breakdown'));
    const hasVisibleRows = visibleRows.length > 0;

    if (toggle) {
      toggle.classList.toggle('is-hidden', !hasVisibleRows);
    }
    if (summaryBreakdown) {
      summaryBreakdown.classList.toggle('is-hidden', !hasVisibleRows);
    }

    const unlockedSavingsCents = visibleRows.reduce((total, row) => {
      const valueNode = row.querySelector('.cart-drawer-custom__summary-breakdown-value');
      if (!valueNode) return total;
      return total + Math.abs(this.parseMoneyTextToCents(valueNode.textContent || '0'));
    }, 0);

    const unlockedDisplay = this.formatMoney(unlockedSavingsCents).replace('.00', '');
    const titleText = `${unlockedDisplay} in savings on this order`;
    const icon = title.querySelector('.cart-drawer-custom__summary-breakdown-title-icon');

    if (icon) {
      title.innerHTML = '';
      title.appendChild(icon);
      title.appendChild(document.createTextNode(titleText));
      return;
    }

    title.textContent = titleText.trim();
  }

  /** @param {string} value */
  parseMoneyTextToCents(value) {
    const normalized = value.replace(/[^0-9.-]/g, '');
    const numeric = Number.parseFloat(normalized);
    if (!Number.isFinite(numeric)) return 0;
    return Math.round(numeric * 100);
  }

  captureSummarySnapshot() {
    const summaryTitle = this.querySelector('.cart-drawer-custom__summary-breakdown-title');
    const summaryPanel = this.querySelector('.cart-drawer-custom__summary-breakdown-panel');

    if (!summaryTitle || !summaryPanel) return null;

    const titleTextNode = Array.from(summaryTitle.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    const titleText = (titleTextNode?.textContent || '').trim();

    const rows = Array.from(summaryPanel.querySelectorAll('.cart-drawer-custom__summary-breakdown-row')).map((row) => {
      const label = row.querySelector('.cart-drawer-custom__summary-breakdown-label');
      const value = row.querySelector('.cart-drawer-custom__summary-breakdown-value');
      return {
        label: (label?.textContent || '').trim().toLowerCase(),
        value: (value?.textContent || '').trim(),
        hidden: row.classList.contains('is-hidden') || row.style.display === 'none',
      };
    });

    return {
      titleText,
      rows,
    };
  }

  captureUpsellSnapshot() {
    const upsellBlock = this.querySelector('[data-gwp-upsell-block]');
    if (!upsellBlock) return null;

    const image = /** @type {HTMLImageElement | null} */ (upsellBlock.querySelector('[data-gwp-upsell-image]'));
    const title = upsellBlock.querySelector('[data-gwp-upsell-title]');
    const addButton = /** @type {HTMLButtonElement | null} */ (upsellBlock.querySelector('[data-gwp-upsell-add]'));
    const addLabel = upsellBlock.querySelector('[data-gwp-upsell-add-label]');

    return {
      display: upsellBlock instanceof HTMLElement ? upsellBlock.style.display : '',
      imageSrc: image?.src || '',
      imageAlt: image?.alt || '',
      title: (title?.textContent || '').trim(),
      addLabel: (addLabel?.textContent || '').trim(),
      variantId: addButton?.dataset.gwpUpsellVariantId || '',
      disabled: Boolean(addButton?.disabled),
    };
  }

  /** @param {{ display: string; imageSrc: string; imageAlt: string; title: string; addLabel: string; variantId: string; disabled: boolean } | null} snapshot */
  applyUpsellSnapshot(snapshot) {
    if (!snapshot) return;

    const upsellBlock = /** @type {HTMLElement | null} */ (this.querySelector('[data-gwp-upsell-block]'));
    if (!upsellBlock) return;

    const image = /** @type {HTMLImageElement | null} */ (upsellBlock.querySelector('[data-gwp-upsell-image]'));
    const title = upsellBlock.querySelector('[data-gwp-upsell-title]');
    const addButton = /** @type {HTMLButtonElement | null} */ (upsellBlock.querySelector('[data-gwp-upsell-add]'));
    const addLabel = upsellBlock.querySelector('[data-gwp-upsell-add-label]');

    upsellBlock.style.display = snapshot.display || '';
    if (image && snapshot.imageSrc) {
      image.src = snapshot.imageSrc;
      image.alt = snapshot.imageAlt;
    }
    if (title && snapshot.title) {
      title.textContent = snapshot.title;
    }
    if (addLabel && snapshot.addLabel) {
      addLabel.textContent = snapshot.addLabel;
    }
    if (addButton) {
      addButton.dataset.gwpUpsellVariantId = snapshot.variantId;
      addButton.disabled = snapshot.disabled;
    }
  }

  /** @param {{ titleText: string; rows: Array<{ label: string; value: string; hidden: boolean }> } | null} snapshot */
  applySummarySnapshot(snapshot) {
    if (!snapshot) return;

    const summaryTitle = this.querySelector('.cart-drawer-custom__summary-breakdown-title');
    const summaryPanel = this.querySelector('.cart-drawer-custom__summary-breakdown-panel');
    if (!summaryTitle || !summaryPanel) return;

    const icon = summaryTitle.querySelector('.cart-drawer-custom__summary-breakdown-title-icon');
    const safeTitleText = snapshot.titleText ? ` ${snapshot.titleText}` : '';

    if (icon) {
      summaryTitle.innerHTML = '';
      summaryTitle.appendChild(icon);
      if (safeTitleText) {
        summaryTitle.appendChild(document.createTextNode(safeTitleText));
      }
    }

    const rowsByLabel = new Map(snapshot.rows.map((row) => [row.label, row]));
    const rows = summaryPanel.querySelectorAll('.cart-drawer-custom__summary-breakdown-row');

    rows.forEach((row) => {
      const labelNode = row.querySelector('.cart-drawer-custom__summary-breakdown-label');
      const valueNode = row.querySelector('.cart-drawer-custom__summary-breakdown-value');
      const labelKey = (labelNode?.textContent || '').trim().toLowerCase();
      const previous = rowsByLabel.get(labelKey);
      if (!previous || !valueNode) return;

      row.classList.toggle('is-hidden', previous.hidden);
      row.style.display = previous.hidden ? 'none' : '';
      valueNode.textContent = previous.value;
    });
  }

  /** @param {Event} event */
  async handleBundleOfferClick(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('.cart-drawer-custom__bundle-offer-action'));
    if (!button || !this.activeCrossSellRule) return;
    const bundleCard = /** @type {HTMLElement | null} */ (button.closest('.cart-drawer-custom__bundle-offer'));

    if (bundleCard?.classList.contains('cart-drawer-custom__loading-card--loading')) return;
    this.setCardLoadingState(bundleCard, true);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.isBundleSwapping = true;
    window.__isSwapping = true;

    try {
      const variantId = Number(this.activeCrossSellRule.bundle_variant_id || 0);
      if (!variantId) {
        throw new Error('Missing cross-sell variant id');
      }

      // Fail fast before any network calls.
      const sellingPlanId = Number(this.activeCrossSellRule.selling_plan_id || 0);
      if (!sellingPlanId) {
        throw new Error('Missing cross-sell selling plan id for bundle subscription');
      }

      // Use cached state — skip a GET /cart.js round-trip.
      const cart = this.latestCartState || await this.fetchCartState();
      const productIdsInCart = new Set(
        (cart?.items || [])
          .map((item) => Number(item?.product_id || 0))
          .filter((id) => Number.isFinite(id) && id > 0)
      );

      const relatedRules = this.crossSellRules.filter((rule) => {
        const ruleBundleVariantId = Number(rule?.bundle_variant_id || 0);
        if (ruleBundleVariantId !== variantId) return false;

        const triggerIds = Array.isArray(rule?.trigger_ids)
          ? [...new Set(rule.trigger_ids.map((id) => Number(id || 0)).filter((id) => Number.isFinite(id) && id > 0))]
          : [];

        if (!triggerIds.length) return false;
        return triggerIds.every((id) => productIdsInCart.has(id));
      });

      const triggerProductIds = new Set();
      relatedRules.forEach((rule) => {
        const triggerIds = Array.isArray(rule?.trigger_ids) ? rule.trigger_ids : [];
        triggerIds.forEach((id) => {
          const productId = Number(id || 0);
          if (Number.isFinite(productId) && productId > 0) {
            triggerProductIds.add(productId);
          }
        });
      });

      if (!triggerProductIds.size) {
        const activeTriggerIds = Array.isArray(this.activeCrossSellRule.trigger_ids)
          ? this.activeCrossSellRule.trigger_ids
          : [];
        activeTriggerIds.forEach((id) => {
          const productId = Number(id || 0);
          if (Number.isFinite(productId) && productId > 0) {
            triggerProductIds.add(productId);
          }
        });
      }

      // Build removal map (sub lines first so sub line is always the one replaced).
      const seenTriggerProductIds = new Set();
      const removalUpdates = (cart?.items || [])
        .slice()
        .sort((a, b) => (Boolean(a?.selling_plan_allocation) ? 0 : 1) - (Boolean(b?.selling_plan_allocation) ? 0 : 1))
        .reduce((acc, item) => {
          const productId = Number(item?.product_id || 0);
          const key = `${item?.key || ''}`;
          if (!triggerProductIds.has(productId) || !key) return acc;
          if (seenTriggerProductIds.has(productId)) return acc;
          seenTriggerProductIds.add(productId);
          acc[key] = 0;
          return acc;
        }, {});

      // Step 1 — add bundle variant.
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, selling_plan: sellingPlanId }] }),
      });
      if (!addResponse.ok) {
        throw new Error('Failed to add cross-sell product to cart');
      }

      // Step 2 — remove trigger items. /cart/update.js returns the full updated cart
      // so no extra GET /cart.js is needed.
      let finalCart = null;
      if (Object.keys(removalUpdates).length > 0) {
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: removalUpdates }),
        });
        if (!updateResponse.ok) {
          throw new Error('Failed to remove trigger products after adding bundle');
        }
        finalCart = await updateResponse.json();
      } else {
        finalCart = await this.fetchCartState();
      }

      this.latestCartState = finalCart;
      await this.refreshDrawerContent(finalCart);
    } catch (error) {
      console.error('Error adding cross-sell product:', error);
    } finally {
      // Reset UI immediately; delay flag reset so TVG's async cart:update re-dispatch
      // (from emitCartUpdated) is still filtered, preventing the drawer from re-opening.
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (bundleCard?.isConnected) {
        this.setCardLoadingState(bundleCard, false);
      }
      window.setTimeout(() => {
        this.isBundleSwapping = false;
        window.__isSwapping = false;
      }, 400);
    }
  }

  /** @param {Event} event */
  async handleSubscribeUpgrade(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('.cart-drawer-custom__item-subscribe-upgrade'));
    if (!button) return;

    const itemContainer = /** @type {HTMLElement | null} */ (button.closest('.cart-drawer-custom__item'));
    if (!itemContainer) return;

    if (itemContainer?.classList.contains('cart-drawer-custom__item--loading')) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await this.subscribeAllEligibleLineItems();
    } catch (error) {
      console.error('Error upgrading cart line items to subscription:', error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Shade collection upgrade (today: Elemental Lip Collection).
   * Adding the bundle is the whole upgrade: bundle-watcher.js intercepts the add and
   * clears the shade lines the collection replaces, so this only has to add and repaint.
   *
   * @param {HTMLButtonElement} button
   * @param {number} variantId
   */
  async handleShadeCollectionUpgrade(button, variantId) {
    const card = /** @type {HTMLElement | null} */ (button.closest('.cart-drawer-custom__item'));
    if (card?.classList.contains('cart-drawer-custom__item--loading')) return;

    const sellingPlanId = Number(button.dataset.shadeUpgradeSellingPlanId || 0);

    button.disabled = true;
    button.classList.add('is-loading');
    if (card) this.setItemLoadingState(card, true);

    try {
      const line = { id: variantId, quantity: 1 };
      if (sellingPlanId > 0) line.selling_plan = sellingPlanId;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] }),
      });
      if (!res.ok) throw new Error('Failed to add shade collection');

      const cart = await this.fetchCartState();
      if (cart) {
        this.applyRealtimeGwpUi(cart);
        this.updateCartCountUI(cart.item_count);
        await this.refreshDrawerContent(cart);
        this.emitCartUpdated(cart);
      } else {
        await this.refreshDrawerContent();
      }
    } catch (error) {
      console.error('[CartDrawer] Shade collection upgrade failed:', error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      if (card?.isConnected) this.setItemLoadingState(card, false);
    }
  }

  /** @param {Event} event */
  async handleBonusAdd(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('.cart-drawer-custom__item-bonus-action'));
    if (!button || button.disabled) return;

    const shadeUpgradeVariantId = Number(button.dataset.shadeUpgradeVariantId || 0);
    if (shadeUpgradeVariantId > 0) {
      await this.handleShadeCollectionUpgrade(button, shadeUpgradeVariantId);
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');

    // Collect ALL qualifying items with qty=1 across the drawer
    const allItemCards = Array.from(this.querySelectorAll('.cart-drawer-custom__item'));
    /** @type {{ card: HTMLElement, lineKey: string }[]} */
    const qtyItems = [];
    /** @type {{ card: HTMLElement, lineKey: string, bonusPlanId: number }[]} */
    const planItems = [];

    for (const card of allItemCards) {
      if (card.dataset.gwpLocked === 'true') continue;
      if (card.dataset.isGiftCard === 'true') continue;
      if (card.dataset.isBundleItem === 'true') continue;
      if (card.dataset.hasSellingPlans !== 'true') continue;

      const qty = Number(card.dataset.quantity || 0);
      if (qty !== 1) continue;

      const lineKey = `${card.dataset.lineItemKey || ''}`;
      if (!lineKey) continue;

      const isSubscribed = card.dataset.isSubscribed === 'true';
      const bonusPlanId = Number(card.dataset.bonusSellingPlanId || 0);

      if (isSubscribed && bonusPlanId > 0) {
        planItems.push({ card, lineKey, bonusPlanId });
      } else {
        qtyItems.push({ card, lineKey });
      }
    }

    const affectedCards = [...qtyItems.map((i) => i.card), ...planItems.map((i) => i.card)];
    affectedCards.forEach((c) => this.setItemLoadingState(c, true));

    try {
      // Batch qty increments (one-time purchase items: 1 → 2)
      if (qtyItems.length > 0) {
        const updates = {};
        qtyItems.forEach(({ lineKey }) => { updates[lineKey] = 2; });
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!res.ok) throw new Error('Failed to update quantities');
      }

      // Sequential selling plan swaps (subscription items → 2-month / 2-pack plan)
      for (const { lineKey, bonusPlanId } of planItems) {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 1, selling_plan: bonusPlanId }),
        });
        if (!res.ok) throw new Error('Failed to update selling plan');
      }

      const cart = await this.fetchCartState();
      if (cart) {
        this.applyRealtimeGwpUi(cart);
        this.updateCartCountUI(cart.item_count);
        await this.refreshDrawerContent(cart);
        this.emitCartUpdated(cart);
      } else {
        await this.refreshDrawerContent();
      }
    } catch (error) {
      console.error('[CartDrawer] Multi-unit bonus action failed:', error);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      affectedCards.forEach((c) => {
        if (c.isConnected) this.setItemLoadingState(c, false);
      });
    }
  }

  async subscribeAllEligibleLineItems() {
    const itemCards = Array.from(this.querySelectorAll('.cart-drawer-custom__item'));

    const updates = itemCards.map((itemCard) => {
      const lineItemKey = `${itemCard.dataset.lineItemKey || ''}`;
      const hasSellingPlans = itemCard.dataset.hasSellingPlans === 'true';
      const isSubscribed = itemCard.dataset.isSubscribed === 'true';
      const isGiftCard = itemCard.dataset.isGiftCard === 'true';
      const isGwpLocked = itemCard.dataset.gwpLocked === 'true';
      const defaultSellingPlanId = Number(itemCard.dataset.defaultSellingPlanId || SUBSCRIPTION_UPGRADE_SELLING_PLAN_ID);

      const quantityInputNode = /** @type {HTMLInputElement | null} */ (itemCard.querySelector('.cart-drawer-custom__quantity-input'));
      const quantityFromText = Number.parseInt(`${quantityInputNode?.value || ''}`.replace(/[^0-9]/g, ''), 10);
      const quantityFromData = Number.parseInt(itemCard.dataset.quantity || '0', 10);
      const quantity = (Number.isFinite(quantityFromText) && quantityFromText > 0)
        ? quantityFromText
        : ((Number.isFinite(quantityFromData) && quantityFromData > 0) ? quantityFromData : 1);

      if (!lineItemKey || !hasSellingPlans || isSubscribed || isGiftCard || isGwpLocked || !Number.isFinite(defaultSellingPlanId) || defaultSellingPlanId <= 0) {
        return null;
      }

      return {
        itemCard,
        lineItemKey,
        defaultSellingPlanId,
        quantity,
      };
    }).filter(Boolean);

    if (!updates.length) {
      return;
    }

    updates.forEach((update) => {
      this.setItemLoadingState(update.itemCard, true);
    });

    try {
      for (const update of updates) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: update.lineItemKey,
            quantity: update.quantity,
            selling_plan: update.defaultSellingPlanId,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to subscribe one of the line items');
        }
      }

      const cart = await this.fetchCartState();
      if (cart) {
        this.applyRealtimeGwpUi(cart);
        this.updateCartCountUI(cart.item_count);
        await this.refreshDrawerContent(cart);
        this.emitCartUpdated(cart);
      } else {
        await this.refreshDrawerContent();
      }
    } finally {
      updates.forEach((update) => {
        if (!update.itemCard?.isConnected) return;
        this.setItemLoadingState(update.itemCard, false);
      });
    }
  }

  /** @param {Event} event */
  async handleSellingPlanChange(event) {
    const select = /** @type {HTMLSelectElement | null} */ (event.target instanceof HTMLSelectElement ? event.target : null);
    if (!select) return;
    const itemContainer = /** @type {HTMLElement | null} */ (select.closest('.cart-drawer-custom__item'));

    const lineItemKey = select.dataset.lineItemKey;
    if (!lineItemKey) return;

    const selectedValue = select.value ? Number(select.value) : null;
    if (selectedValue !== null && !Number.isFinite(selectedValue)) return;

    if (itemContainer?.classList.contains('cart-drawer-custom__item--loading')) return;

    this.setItemLoadingState(itemContainer, true);
    select.disabled = true;
    select.setAttribute('aria-busy', 'true');

    try {
      await this.updateLineItemSellingPlan(lineItemKey, selectedValue);
    } catch (error) {
      console.error('Error changing selling plan:', error);
    } finally {
      select.disabled = false;
      select.removeAttribute('aria-busy');
      if (itemContainer?.isConnected) {
        this.setItemLoadingState(itemContainer, false);
      }
    }
  }

  /** @param {string} lineItemKey @param {number | null} sellingPlanId */
  async updateLineItemSellingPlan(lineItemKey, sellingPlanId) {
    const itemContainer = /** @type {HTMLElement | null} */ (this.querySelector(`.cart-drawer-custom__item[data-line-item-key="${lineItemKey}"]`));
    const quantityInput = /** @type {HTMLInputElement | null} */ (itemContainer?.querySelector('.cart-drawer-custom__quantity-input'));
    const currentQuantity = Number.parseInt(`${quantityInput?.value || ''}`.replace(/[^0-9]/g, ''), 10) || 1;

    const payload = {
      id: lineItemKey,
      quantity: currentQuantity,
    };

    if (sellingPlanId) {
      payload.selling_plan = sellingPlanId;
    } else {
      payload.selling_plan = null;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to update selling plan');
    }

    const cart = await response.json();
    this.applyRealtimeGwpUi(cart);
    this.updateCartCountUI(cart.item_count);
    await this.refreshDrawerContent(cart);
    this.emitCartUpdated(cart);
  }

  /** @param {{ items?: Array<any>; items_subtotal_price?: number; total_price?: number } | null} cart */
  applyRealtimeGwpUi(cart) {
    if (!cart) return;

    this.latestCartState = cart;
    const subtotalCents = this.getThresholdSubtotalCents(cart);
    this.renderGwpProgress(subtotalCents);
    this.renderGwpRewards(cart, subtotalCents);
    this.renderGwpUpsell(cart);
    this.updateSummaryDynamic(cart);
  }
}

// Register the custom element
customElements.define('cart-drawer-custom', CartDrawerCustom);

// Global functions for backward compatibility
/** @type {Window & { openCartDrawer?: () => void; closeCartDrawer?: () => void }} */
const win = window;
win.openCartDrawer = function () {
  const drawer = /** @type {CartDrawerCustom | null} */ (document.querySelector('cart-drawer-custom:not(.cart-drawer-custom--page)'));
  if (drawer) drawer.open();
};

win.closeCartDrawer = function () {
  const drawer = /** @type {CartDrawerCustom | null} */ (document.querySelector('cart-drawer-custom:not(.cart-drawer-custom--page)'));
  if (drawer) drawer.close();
};